import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TableLoading } from '../Loading';
import styles from './UserPage.module.css';
import { getUserById, UserById } from '../../services/getUserById.service';
import {
  getBalanceHistoryByUserId,
  BalanceHistoryItem,
} from '../../services/balanceHistory.service';
import {
  getChildBalanceReport,
  ChildBalanceReport,
  EMPTY_CHILD_BALANCE_REPORT,
} from '../../services/getChildBalanceReport.service';
import { getAllUsers, User } from '../../services/getAllUsers.service';
import {
  allBalancesSubscriptionService,
  AllBalancesUpdatedData,
  stopAllBalancesSubscription
} from '../../services/allBalancesSubscription.service';
import { authService } from '../../services/auth.service';
import {
  getUsersByRoleParentId,
  getUsersWithFiltersByRole,
  User as RoleBasedUser,
} from '../../services/roleBasedUsers.service';
import {
  getGameSessions,
  GameSession,
} from '../../services/getGameSessions.service';
import { formatNumberWithCommas } from '../../utils/helpers/formatting.helpers';
import { formatLoginTime } from '../../utils/helpers/date.helpers';
import {
  EditIcon,
  ChangePasswordIcon,
  ProfitIcon,
  InIcon,
  OutIcon,
} from '../../assets/icons';
import Modal from './Modal';
import CashInOutForm from './CashInOutForm';
import ChangePasswordModal from './ChangePasswordModal';
import { updateUser, updateUserService } from '../../services/updateUser.service';
import { getErrorMessage } from '../../utils/helpers/getErrorMessage';
import {
  getUserActivity,
  UserActivity,
} from '../../services/getUserActivity.service';
import { Pagination, PaginationItem, Stack } from '@mui/material';
import { BalanceUpdatedData, startBalanceSubscription, startBalanceSubscriptionForPlayer } from '../../services/balanceSubscription.service';

const getEnvironmentDisplayName = (name: string | undefined) =>
  name === 'Production' ? 'Flow888' : name;

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      const mobile = window.innerWidth <= 820;
      setIsMobile(mobile);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

const UserPage: React.FC = () => {
  function getLastWeekDate() {
    const today = new Date(); // current date
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);

    // format to YYYY-MM-DD
    const year = lastWeek.getFullYear();
    const month = String(lastWeek.getMonth() + 1).padStart(2, '0');
    const day = String(lastWeek.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  function formatDate(date: any) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // YYYY-MM-DD
  }
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [activeSubTab, setActiveSubTab] = useState<
    'main' | 'transactions' | 'subCustomers' | 'gameReports' | 'onlineTransactions'
  >('main');

  const [user, setUser] = useState<UserById | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalType, setModalType] = useState<null | 'in' | 'out'>(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [balanceHistory, setBalanceHistory] = useState<BalanceHistoryItem[]>(
    [],
  );
  const [dateFrom, setDateFrom] = useState(getLastWeekDate());
  const [dateTo, setDateTo] = useState(formatDate(new Date()));
  const [timeFrom, setTimeFrom] = useState('00:00');
  const [timeTo, setTimeTo] = useState('23:59');
  const [balanceHistoryLoading, setBalanceHistoryLoading] = useState(true);
  const [editInfoMode, setEditInfoMode] = useState(false);
  const [editName, setEditName] = useState(user?.name || '');
  const [editUsername, setEditUsername] = useState(user?.username || '');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [subCustomers, setSubCustomers] = useState<RoleBasedUser[]>([]);
  const [totalBalance, setTotalBalance] = useState(0);
  const [totalBalanceIn, setTotalBalanceIn] = useState(0);
  const [totalBalanceOut, setTotalBalanceOut] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [subCustomersLoading, setSubCustomersLoading] = useState(false);
  const [gameReports, setGameReports] = useState<GameSession[]>([]);
  const [gameReportsLoading, setGameReportsLoading] = useState(false);
  const [cashOperationError, setCashOperationError] = useState<string | null>(
    null,
  );
  const [refetchingUser, setRefetchingUser] = useState(false);
  const [userActivity, setUserActivity] = useState<UserActivity[]>([]);
  const [userActivityLoading, setUserActivityLoading] = useState(true);
  const [userActivityError, setUserActivityError] = useState<string | null>(
    null,
  );
  const [userActivityPageInfo, setUserActivityPageInfo] = useState<any>(null);
  // Pagination state for user activity
  const [userActivityHasNextPage, setUserActivityHasNextPage] = useState(false);
  const [userActivityEndCursor, setUserActivityEndCursor] = useState<
    string | null
  >(null);
  const [userActivityLoadingMore, setUserActivityLoadingMore] = useState(false);
  const activityListRef = React.useRef<HTMLDivElement>(null);

  // Add pagination state for transactions
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [subCustomersTotal, setSubCustomersTotal] = useState(0);
  const TRANSACTIONS_PAGE_SIZE = 10;

  // Add pagination state for game reports
  const [gameReportsPage, setGameReportsPage] = useState(1);
  const [gameReportsTotal, setGameReportsTotal] = useState(0);
  const GAME_REPORTS_PAGE_SIZE = 10;
  const [subCustomersPage, setSubCustomersPage] = useState(1);
  // Date/Time states for subCustomers search
  const [subCustomersDateFrom, setSubCustomersDateFrom] = useState(getLastWeekDate());
  const [subCustomersDateTo, setSubCustomersDateTo] = useState(formatDate(new Date()));
  const [subCustomersTimeFrom, setSubCustomersTimeFrom] = useState('00:00');
  const [subCustomersTimeTo, setSubCustomersTimeTo] = useState('23:59');
  const [subCustomersNameSearch, setSubCustomersNameSearch] = useState('');
  const [siblingCount, setSiblingCount] = useState(2);

  // Online Transactions state
  const [onlineTransactions, setOnlineTransactions] = useState<BalanceHistoryItem[]>([]);
  const [onlineTransactionsLoading, setOnlineTransactionsLoading] = useState(true);
  const [onlineTransactionsPage, setOnlineTransactionsPage] = useState(1);
  const [onlineTransactionsTotal, setOnlineTransactionsTotal] = useState(0);
  const [onlineDateFrom, setOnlineDateFrom] = useState(getLastWeekDate());
  const [onlineDateTo, setOnlineDateTo] = useState(formatDate(new Date()));
  const [onlineTimeFrom, setOnlineTimeFrom] = useState('00:00');
  const [onlineTimeTo, setOnlineTimeTo] = useState('23:59');
  const ONLINE_TRANSACTIONS_PAGE_SIZE = 10;

  // Report section date range and summary
  const [reportDateFrom, setReportDateFrom] = useState(getLastWeekDate());
  const [reportDateTo, setReportDateTo] = useState(formatDate(new Date()));
  const [reportTimeFrom, setReportTimeFrom] = useState('00:00');
  const [reportTimeTo, setReportTimeTo] = useState('23:59');
  const [reportLoading, setReportLoading] = useState(false);
  // Server-side balance report (cash / crypto / total) shared by the Main,
  // Transactions and Online Transactions tabs. Replaces the old client-side
  // summing that fetched up to 20k rows per search.
  const [childBalanceReport, setChildBalanceReport] = useState<ChildBalanceReport>(
    EMPTY_CHILD_BALANCE_REPORT,
  );

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 890) {
        setSiblingCount(0); // mobile: only show current page
      } else if (window.innerWidth < 1080) {
        setSiblingCount(1); // tablet: show 1 on each side
      } else {
        setSiblingCount(2); // desktop: show 2 on each side
      }
    };

    handleResize(); // run once
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (id) {
      getUserById(parseInt(id))
        .then((userData) => {
          setUser(userData);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching user:', err);
          setError(getErrorMessage(err));
          setLoading(false);
        });
    }
  }, [id]);

  // Start balance subscription for the main user when user data is available
  useEffect(() => {
    const currentUser = authService.getStoredUser();

    if (user && currentUser?.id) {
      console.log('👤 UserPage: Starting balance subscription for user:', user.id);
      startBalanceSubscriptionForPlayer({
        onUpdate: (data: BalanceUpdatedData) => {
          console.log('👤 UserPage: Received balance update:', data);

          if (user && data.id === user.id) {
            const updatedUser: UserById = {
              ...user,
              ...(data.Balance && { Balance: data.Balance }),
              ...(typeof data.balanceIn === 'number' && { balanceIn: data.balanceIn }),
              ...(typeof data.balanceOut === 'number' && { balanceOut: data.balanceOut }),
              ...(typeof data.credit === 'number' && { credit: data.credit }),
              ...(typeof data.isActive === 'boolean' && { isActive: data.isActive }),
              ...(data.lastLogin !== undefined && { lastLogin: data.lastLogin }),
              ...(data.loginIp !== undefined && { loginIp: data.loginIp }),
            };
            setUser(updatedUser);
          }
        },
        onError: (error) => {
          console.error('❌ Balance subscription error:', error);
          // Optionally implement fallback polling here
        },
        onConnect: () => {
          console.log('🔌 Balance subscription connected');
        },
        onDisconnect: () => {
          console.log('🔌 Balance subscription disconnected');
        }
      }, user.id);
    }

    // Cleanup subscription when component unmounts or user changes
    return () => {
      if (user) {
        stopAllBalancesSubscription().catch(error => {
          console.error('👤 UserPage: Error stopping balance subscription:', error);
        });
      }
    };
  }, [user]);

  // Fetch balance history for the user (for main tab)
  useEffect(() => {
    if (user && activeSubTab === 'main') {
      setBalanceHistoryLoading(true);
      let From = dateFrom;
      let To = dateTo;

      if(timeFrom) {
        From = `${dateFrom}T${timeFrom}`;
      }
      if(timeTo) {
        To = `${dateTo}T${timeTo}`;
      }

      getBalanceHistoryByUserId(1, ['TOP_UP_BALANCE', 'WITHDRAW_BALANCE'], 20, 0, From, To)
        .then((res: any) => {
          setBalanceHistory(res.history);
        })
        .catch((err: any) => {
          setBalanceHistory([]);
        })
        .finally(() => setBalanceHistoryLoading(false));
    }
    // Clear balance history if not on main tab
    // if (activeSubTab !== 'main') {
    //   setBalanceHistory([]);
    // }
  }, [user, activeSubTab]);

  // Fetch all balance history for Transactions tab (with pagination)
  useEffect(() => {
    if (user && activeSubTab === 'transactions') {
      setBalanceHistoryLoading(true);
      let From = dateFrom;
      let To = dateTo;

      if(timeFrom) {
        From = `${dateFrom}T${timeFrom}`;
      }
      if(timeTo) {
        To = `${dateTo}T${timeTo}`;
      }

      getBalanceHistoryByUserId(user.id, ['TOP_UP_BALANCE', 'WITHDRAW_BALANCE'], 10, (transactionsPage - 1) * TRANSACTIONS_PAGE_SIZE, From, To)
        // getBalanceHistory({
        //   userId: 17,
        //   includeTypes: ['TOP_UP_BALANCE', 'WITHDRAW_BALANCE'],
        //   limit: TRANSACTIONS_PAGE_SIZE,
        //   offset: (transactionsPage - 1) * TRANSACTIONS_PAGE_SIZE,
        // })
        .then((res: any) => {
          setBalanceHistory(res.history);
          setTransactionsTotal(res.total || 0);
        })
        .catch((err: any) => {
          setBalanceHistory([]);
          setTransactionsTotal(0);
        })
        .finally(() => setBalanceHistoryLoading(false));
      fetchChildBalanceReport(From, To);
    }
    // Clear balance history if not on transactions tab
    if (activeSubTab !== 'transactions') {
      setBalanceHistory([]);
      setTransactionsTotal(0);
      setTransactionsPage(1);
    }
  }, [user, activeSubTab, transactionsPage]);

  // Fetch online transactions (CRIPTO_DEPOSIT, CRIPTO_WITHDRAW)
  useEffect(() => {
    if (user && activeSubTab === 'onlineTransactions') {
      setOnlineTransactionsLoading(true);
      let From = onlineDateFrom;
      let To = onlineDateTo;

      if (onlineTimeFrom) {
        From = `${onlineDateFrom}T${onlineTimeFrom}`;
      }
      if (onlineTimeTo) {
        To = `${onlineDateTo}T${onlineTimeTo}`;
      }

      getBalanceHistoryByUserId(user.id, ['CRIPTO_DEPOSIT', 'CRIPTO_WITHDRAW'], ONLINE_TRANSACTIONS_PAGE_SIZE, (onlineTransactionsPage - 1) * ONLINE_TRANSACTIONS_PAGE_SIZE, From, To)
        .then((res: any) => {
          setOnlineTransactions(res.history);
          setOnlineTransactionsTotal(res.total || 0);
        })
        .catch((err: any) => {
          setOnlineTransactions([]);
          setOnlineTransactionsTotal(0);
        })
        .finally(() => setOnlineTransactionsLoading(false));
      fetchChildBalanceReport(From, To);
    }
    if (activeSubTab !== 'onlineTransactions') {
      setOnlineTransactions([]);
      setOnlineTransactionsTotal(0);
      setOnlineTransactionsPage(1);
    }
  }, [user, activeSubTab, onlineTransactionsPage]);

  // Build additional filters for subCustomers
  const buildSubCustomersFilters = () => {
    const filters: any = {};

    if (subCustomersNameSearch.trim()) {
      filters.search = subCustomersNameSearch.trim();
    }

    if (subCustomersDateFrom) {
      let startDateTime = subCustomersDateFrom;
      if (subCustomersTimeFrom) {
        startDateTime = `${subCustomersDateFrom}T${subCustomersTimeFrom}`;
      }
      filters.startDate = startDateTime;
    }

    if (subCustomersDateTo) {
      let endDateTime = subCustomersDateTo;
      if (subCustomersTimeTo) {
        endDateTime = `${subCustomersDateTo}T${subCustomersTimeTo}`;
      }
      filters.endDate = endDateTime;
    }

    return filters;
  };

  // Fetch sub customers when subCustomers tab is active
  const fetchSubCustomers = async () => {
    if (!user) return;
    setSubCustomersLoading(true);
    const subCustomer = user.role?.name === 'DISTRIBUTOR' ? 'SUBDISTRIBUTOR' :
      user.role?.name === 'SUBDISTRIBUTOR' ? 'AGENT' :
        user.role?.name === 'AGENT' ? 'SHOP' : "USER";
    
    const additionalFilters = buildSubCustomersFilters();
    
    try {
      const data = await getUsersWithFiltersByRole(subCustomer, {
        ...additionalFilters,
        parentId: user.id,
      }, {
        first: 10,
        after: (subCustomersPage - 1) * 10,
      });
      
      setSubCustomersTotal(data.pageInfo.total || 0);
      setSubCustomers(data.users);

      // Update total balances from the response
      setTotalBalance(data.totalBalance || 0);
      setTotalBalanceIn(data.totalBalanceIn || 0);
      setTotalBalanceOut(data.totalBalanceOut || 0);
      setTotalProfit(data.totalProfit || 0);
    } catch (err) {
      console.error('Error fetching sub customers:', err);
      setSubCustomers([]);
    } finally {
      setSubCustomersLoading(false);
    }
  };

  useEffect(() => {
    if (activeSubTab === 'subCustomers' && user) {
      fetchSubCustomers();
    }
    // Clear sub customers if not on subCustomers tab
    if (activeSubTab !== 'subCustomers') {
      setSubCustomers([]);
      setSubCustomersTotal(0);
      setSubCustomersPage(1);
      setTotalBalance(0);
      setTotalBalanceIn(0);
      setTotalBalanceOut(0);
      setTotalProfit(0);
    }
  }, [activeSubTab, user, subCustomersPage]);

  // WebSocket subscription for real-time balance updates for subCustomers
  useEffect(() => {
    let isSubscribed = true;

    if (activeSubTab === 'subCustomers' && subCustomers.length > 0 && user) {
      const userIds = subCustomers.map(customer => customer.id);
      const currentUser = authService.getStoredUser();

      if (!currentUser?.id) {
        return;
      }


      allBalancesSubscriptionService.startSubscription(
        {
          userIds,
          currentUserId: currentUser.id,
        },
        {
          onConnect: () => {
            console.log('👤🏢 UserPage balance subscription connected');
          },
          onUpdate: (data: AllBalancesUpdatedData) => {
            if (!isSubscribed) return;


            // Update subCustomers with new balance data
            setSubCustomers(prevCustomers => {
              return prevCustomers.map(customer => {
                const updatedUser = data.users.find(u => u.id === customer.id);
                if (updatedUser) {
                  return {
                    ...customer,
                    Balance: updatedUser.Balance ? {
                      id: updatedUser.Balance.id,
                      amount: updatedUser.Balance.amount,
                      credit: customer.Balance?.credit || 0, // Keep existing credit value
                      onHold: updatedUser.Balance.onHold,
                    } : customer.Balance,
                    balanceIn: updatedUser.balanceIn || customer.balanceIn,
                    balanceOut: updatedUser.balanceOut || customer.balanceOut,
                    credit: updatedUser.credit,
                    isActive: updatedUser.isActive,
                    lastLogin: updatedUser.lastLogin || customer.lastLogin,
                    loginIp: updatedUser.loginIp || customer.loginIp,
                  };
                }
                return customer;
              });
            });
          },
          onError: (error) => {
            console.error('👤🏢 UserPage balance subscription error:', error);
          },
          onDisconnect: () => {
            console.log('👤🏢 UserPage balance subscription disconnected');
          }
        }
      ).catch(error => {
        console.error('👤🏢 Failed to start UserPage balance subscription:', error);
      });
    }

    return () => {
      isSubscribed = false;
      if (activeSubTab !== 'subCustomers' || subCustomers.length === 0) {
        allBalancesSubscriptionService.stopSubscription().catch(error => {
          console.error('👤🏢 Error stopping UserPage balance subscription:', error);
        });
      }
    };
  }, [activeSubTab, subCustomers, user]);

  // Cleanup subscriptions on component unmount
  useEffect(() => {
    return () => {
      // Stop main user balance subscription
      stopAllBalancesSubscription().catch(error => {
        console.error('👤 Error stopping main UserPage balance subscription on unmount:', error);
      });

      // Stop subCustomers balance subscription
      allBalancesSubscriptionService.stopSubscription().catch(error => {
        console.error('👤🏢 Error stopping subCustomers balance subscription on unmount:', error);
      });
    };
  }, []);

  // Fetch report summary when main (Reports) tab is active
  useEffect(() => {
    if (activeSubTab === 'main' && user) {
      handleReportSearch();
    }
  }, [activeSubTab, user]);

  // Fetch game reports when gameReports tab is active
  useEffect(() => {
    if (activeSubTab === 'gameReports' && user) {
      setGameReportsLoading(true);
      getGameSessions({
        userId: user.id,
        limit: GAME_REPORTS_PAGE_SIZE,
        offset: (gameReportsPage - 1) * GAME_REPORTS_PAGE_SIZE,
      })
        .then((data) => {
          setGameReports(data.sessions);
          setGameReportsTotal(data.total || 0);
        })
        .catch((err) => {
          console.error('Error fetching game reports:', err);
          setGameReports([]);
          setGameReportsTotal(0);
        })
        .finally(() => setGameReportsLoading(false));
    }
    // Clear game reports if not on gameReports tab
    if (activeSubTab !== 'gameReports') {
      setGameReports([]);
      setGameReportsTotal(0);
      setGameReportsPage(1);
    }
  }, [activeSubTab, user, gameReportsPage]);

  // Redirect from Game Reports tab if user is not USER role
  useEffect(() => {
    if (user && activeSubTab === 'gameReports' && user.role?.name !== 'USER') {
      setActiveSubTab('main');
    }
  }, [user, activeSubTab]);

  useEffect(() => {
    if (user) {
      setEditName(user.name || '');
      setEditUsername(user.username || '');
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setUserActivityLoading(true);
      setUserActivityError(null);
      getUserActivity(user.id, 10)
        .then((res) => {
          setUserActivity(res.activities);
          setUserActivityPageInfo(res.pageInfo);
          setUserActivityHasNextPage(res.pageInfo?.hasNextPage || false);
          setUserActivityEndCursor(res.pageInfo?.endCursor || null);
        })
        .catch((err) => {
          setUserActivity([]);
          setUserActivityError(getErrorMessage(err));
          setUserActivityHasNextPage(false);
          setUserActivityEndCursor(null);
        })
        .finally(() => setUserActivityLoading(false));
    }
  }, [user]);

  // Load more user activity (pagination)
  const loadMoreUserActivity = async () => {
    if (!user || !userActivityHasNextPage || userActivityLoadingMore) return;
    setUserActivityLoadingMore(true);
    try {
      const res = await getUserActivity(
        user.id,
        10,
        userActivityEndCursor || undefined,
      );
      setUserActivity((prev) => [...prev, ...(res.activities || [])]);
      setUserActivityHasNextPage(res.pageInfo?.hasNextPage || false);
      setUserActivityEndCursor(res.pageInfo?.endCursor || null);
    } catch (err) {
      setUserActivityError(getErrorMessage(err));
    } finally {
      setUserActivityLoadingMore(false);
    }
  };

  // Scroll handler for activity list
  const handleUserActivityScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      userActivityHasNextPage &&
      !userActivityLoadingMore &&
      !userActivityLoading
    ) {
      loadMoreUserActivity();
    }
  };

  const handleEditInfo = () => {
    setEditInfoMode(true);
    setEditError('');
  };

  const handleCancelEdit = () => {
    setEditInfoMode(false);
    setEditName(user?.name || '');
    setEditUsername(user?.username || '');
    setEditError('');
  };

  const handleSaveEdit = async () => {
    if (!user) return;
    setEditLoading(true);
    setEditError('');
    try {
      await updateUser({ id: user.id, name: editName, username: editUsername });
      setEditInfoMode(false);
      setUser({ ...user, name: editName, username: editUsername });
    } catch (err: any) {
      setEditError(err?.message || 'Failed to update info');
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;
    setStatusLoading(true);
    setEditError('');
    try {
      const newStatus = !user.isActive;
      await updateUser({ id: user.id, isActive: newStatus });
      setUser({ ...user, isActive: newStatus });
    } catch (err: any) {
      setEditError(err?.message || 'Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleCashOperationSuccess = async (response: any) => {
    // Refresh user data to get updated balance
    if (!user) return;
    setRefetchingUser(true);
    setCashOperationError(null);
    try {
      // Add a delay to ensure backend has processed the transaction
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Clear Apollo cache for this user to force fresh data
      const { apolloClient } = await import('../../config/apollo-client');
      apolloClient.cache.evict({
        fieldName: 'user',
        args: { id: user.id },
      });
      apolloClient.cache.gc();

      const updatedUser = await getUserById(user.id);
      setUser(updatedUser);
      setModalType(null);

      // --- Immediate sidebar balance update logic ---
      // Import authService and getCurrentUserBalance dynamically to avoid circular deps
      const { authService } = await import('../../services/auth.service');
      const { getCurrentUserBalance } = await import(
        '../../services/getCurrentUserBalance.service'
      );

      // Always update the sidebar balance immediately after cash in/out operation
      try {
        const newBalance = await getCurrentUserBalance();

        // if (currentUser) {
        //   // authService.setCurrentUser(updatedCurrentUser);

        //   // Force a re-render of the sidebar by triggering a custom event
        //   window.dispatchEvent(new CustomEvent('balanceUpdated'));
        // }
        window.dispatchEvent(new CustomEvent('balanceUpdated'));

      } catch (balanceError) {
        console.error('❌ Error updating sidebar balance:', balanceError);
        // Don't fail the entire operation if balance update fails
      }
      // --- End sidebar balance update logic ---
    } catch (err) {
      console.error('Error refreshing user data:', err);
      setCashOperationError(
        'Operation successful but failed to refresh user data',
      );
    } finally {
      setRefetchingUser(false);
    }
  };

  const handleCashOperationError = (error: any) => {
    setCashOperationError(getErrorMessage(error));
  };

  const handleSubCustomerClick = (userId: number) => {
    setActiveSubTab('main');
    navigate(`/user/${userId}`);
  };

  // Handle subCustomers search
  const handleSubCustomersSearch = () => {
    setSubCustomersPage(1);
    fetchSubCustomers();
  };

  // Clear subCustomers filters
  const handleClearSubCustomersFilters = () => {
    setSubCustomersNameSearch('');
    setSubCustomersDateFrom(getLastWeekDate());
    setSubCustomersDateTo(formatDate(new Date()));
    setSubCustomersTimeFrom('00:00');
    setSubCustomersTimeTo('23:59');
    setSubCustomersPage(1);
    // Fetch will be triggered by the useEffect when page changes
  };

  const handleWagerEnabledChange = (id: number) => {
    const currentUser = subCustomers?.find((d) => d.id === id);
    const isWagerEnabled = currentUser ? !currentUser.isWagerEnabled : false;

    if (subCustomers) {
      const updatedSubCustomers = subCustomers.map((user) =>
        user.id === id
          ? { ...user, isWagerEnabled }
          : user
      );
      setSubCustomers(updatedSubCustomers);
    }

    updateUserService.updateUser({
      id,
      isWagerEnabled,
    });
  };
  const handleCashbackEnabledChange = (id: number) => {
    const currentUser = subCustomers?.find((d) => d.id === id);
    const isCashbackEnabled = currentUser ? !currentUser.isCashbackEnabled : false;

    if (subCustomers) {
      const updatedSubCustomers = subCustomers.map((user) =>
        user.id === id
          ? { ...user, isCashbackEnabled }
          : user
      );
      setSubCustomers(updatedSubCustomers);
    }

    updateUserService.updateUser({
      id,
      isCashbackEnabled,
    });
  };

  // Fetch the server-side balance report (cash / crypto / total) for a date range.
  const fetchChildBalanceReport = async (from: string, to: string) => {
    if (!user) return;
    setReportLoading(true);
    try {
      const report = await getChildBalanceReport({
        userId: user.id,
        startDate: from,
        endDate: to,
        environmentID: null,
      });
      setChildBalanceReport(report);
    } catch (error) {
      console.error('Error fetching child balance report:', error);
      setChildBalanceReport(EMPTY_CHILD_BALANCE_REPORT);
    } finally {
      setReportLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!user) return;
    setBalanceHistoryLoading(true);
    try {
      let From = dateFrom;
      let To = dateTo;

      if(timeFrom) {
        From = `${dateFrom}T${timeFrom}`;
      }
      if(timeTo) {
        To = `${dateTo}T${timeTo}`;
      }


      const data = await getBalanceHistoryByUserId(user.id, ['TOP_UP_BALANCE', 'WITHDRAW_BALANCE'], 10, (transactionsPage - 1) * TRANSACTIONS_PAGE_SIZE, From, To)
      setBalanceHistory(data.history);
      setTransactionsTotal(data.total || 0);
      setBalanceHistoryLoading(false);
      setTransactionsPage(1);
      fetchChildBalanceReport(From, To);
    } catch (error) {
      console.error('Error fetching balance history:', error);
      setBalanceHistoryLoading(false);
    }
  }

  const handleOnlineSearch = async () => {
    if (!user) return;
    setOnlineTransactionsLoading(true);
    try {
      let From = onlineDateFrom;
      let To = onlineDateTo;
      
      if (onlineTimeFrom) {
        From = `${onlineDateFrom}T${onlineTimeFrom}`;
      }
      if (onlineTimeTo) {
        To = `${onlineDateTo}T${onlineTimeTo}`;
      }

      const data = await getBalanceHistoryByUserId(user.id, ['CRIPTO_DEPOSIT', 'CRIPTO_WITHDRAW'], ONLINE_TRANSACTIONS_PAGE_SIZE, (onlineTransactionsPage - 1) * ONLINE_TRANSACTIONS_PAGE_SIZE, From, To);
      setOnlineTransactions(data.history);
      setOnlineTransactionsTotal(data.total || 0);
      setOnlineTransactionsLoading(false);
      setOnlineTransactionsPage(1);
      fetchChildBalanceReport(From, To);
    } catch (error) {
      console.error('Error fetching online transactions:', error);
      setOnlineTransactionsLoading(false);
    }
  };

  const handleReportSearch = async () => {
    if (!user) return;

    let From = reportDateFrom;
    let To = reportDateTo;

    if (reportTimeFrom) {
      From = `${reportDateFrom}T${reportTimeFrom}`;
    }
    if (reportTimeTo) {
      To = `${reportDateTo}T${reportTimeTo}`;
    }

    await fetchChildBalanceReport(From, To);
  };

  // Shared cash / online / total summary table fed by getChildBalanceReport.
  const renderBalanceReportTable = () => (
    <div className={styles.tableWrapper}>
      {reportLoading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>Loading report...</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th></th>
              <th className={styles.inCol}>In</th>
              <th className={styles.outCol}>Out</th>
              <th>total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td data-label="Type" style={{ fontWeight: 'bold' }}>cash</td>
              <td className={styles.inCol} data-label="In">
                {formatNumberWithCommas(childBalanceReport.cashCashIn)}
              </td>
              <td className={styles.outCol} data-label="Out">
                {formatNumberWithCommas(childBalanceReport.cashCashOut)}
              </td>
              <td data-label="Total" style={{ fontWeight: 'bold' }}>
                {formatNumberWithCommas(childBalanceReport.cashProfit)}
              </td>
            </tr>
            <tr>
              <td data-label="Type" style={{ fontWeight: 'bold' }}>online</td>
              <td className={styles.inCol} data-label="In">
                {formatNumberWithCommas(childBalanceReport.cryptoCashIn)}
              </td>
              <td className={styles.outCol} data-label="Out">
                {formatNumberWithCommas(childBalanceReport.cryptoCashOut)}
              </td>
              <td data-label="Total" style={{ fontWeight: 'bold' }}>
                {formatNumberWithCommas(childBalanceReport.cryptoProfit)}
              </td>
            </tr>
            <tr style={{ background: '#f5f5f5', fontWeight: 'bold' }}>
              <td></td>
              <td className={styles.inCol} data-label="In">
                {formatNumberWithCommas(childBalanceReport.totalCashIn)}
              </td>
              <td className={styles.outCol} data-label="Out">
                {formatNumberWithCommas(childBalanceReport.totalCashOut)}
              </td>
              <td data-label="Total" style={{ fontWeight: 'bold', background: '#ffff00' }}>
                {formatNumberWithCommas(childBalanceReport.totalProfit)}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );

  if (loading) {
    return (
      <main className={styles.main}>
        <div className={styles.loadingContainer}>Loading user data...</div>
      </main>
    );
  }

  if (error || !user) {
    return (
      <main className={styles.main}>
        <div className={styles.errorContainer}>
          <div className={styles.errorMessage}>{error || 'User not found'}</div>
          <button
            onClick={() => navigate('/distributor')}
            className={styles.backButton}
          >
            Back to Distributors
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        Distributor {'>'}{' '}
        {activeSubTab === 'subCustomers'
          ? 'Sub Customers'
          : activeSubTab === 'gameReports' ? 'Game Reports' : activeSubTab === 'onlineTransactions' ? 'Online Transactions' : activeSubTab === 'main' ? 'Main Reports' : 'Transactions'}
        {/* {activeSubTab === 'main' && ' > Main Reports'}
        {activeSubTab === 'transactions' && ' > Transactions'} */}
      </div>
      {/* Top Navigation Tabs */}
      <div className={styles.tabsRow}>
        <div
          className={
            activeSubTab === 'main'
              ? styles.tabActive
              : styles.tab
          }
          onClick={() => setActiveSubTab('main')}
          style={{ cursor: 'pointer' }}
        >
          Reports
        </div>
        {user && user?.role?.name !== 'USER' && (

          <div
            className={
              activeSubTab === 'subCustomers' ? styles.tabActive : styles.tab
            }
            onClick={() => setActiveSubTab('subCustomers')}
            style={{ cursor: 'pointer' }}
          >
            Sub Customers
          </div>
        )}
        {user?.role?.name === 'USER' && (
          <div
            className={
              activeSubTab === 'gameReports' ? styles.tabActive : styles.tab
            }
            onClick={() => setActiveSubTab('gameReports')}
            style={{ cursor: 'pointer' }}
          >
            Game Reports
          </div>
        )}
        <div
          className={
            activeSubTab === 'transactions' ? styles.tabActive : styles.tab
          }
          onClick={() => setActiveSubTab('transactions')}
        >
          Transactions
        </div>
        <div
          className={
            activeSubTab === 'onlineTransactions' ? styles.tabActive : styles.tab
          }
          onClick={() => setActiveSubTab('onlineTransactions')}
        >
          Online Transactions
        </div>
      </div>
      {/* Sub-tabs - only show when Reports tab is active */}
      {activeSubTab === 'main' || activeSubTab === 'transactions' ? (
        <div className={styles.subTabsRow}>
          {/* <div
            className={
              activeSubTab === 'main' ? styles.subTabActive : styles.subTab
            }
            onClick={() => setActiveSubTab('main')}
          >
            Main Reports
          </div> */}
          {/* <div
            className={
              activeSubTab === 'transactions'
                ? styles.subTabActive
                : styles.subTab
            }
            onClick={() => setActiveSubTab('transactions')}
          >
            Transactions
          </div> */}
        </div>
      ) : null}

      {activeSubTab === 'main' && (
        // Main Reports View
        <>
          {/* Main Content Row */}
          <div className={styles.contentRow}>
            {/* Left: Profile & Profit */}
            <div className={styles.leftCol}>
              <div className={styles.profileCard}>
                {/* Desktop: Show profile edit links only on desktop */}
                {!isMobile && (
                  <>
                    <div className={styles.profileName}>
                      {user.username.toUpperCase()}
                    </div>

                    <div className={styles.profileEditLinks}>
                      <div
                        className={styles.profileLink}
                        onClick={handleEditInfo}
                        style={{ cursor: 'pointer' }}
                      >
                        Edit Info{' '}
                        <EditIcon
                          style={{ verticalAlign: 'middle', marginLeft: 4 }}
                        />
                      </div>
                      <div
                        className={styles.profileLink}
                        onClick={() => setShowChangePassword(true)}
                        style={{ cursor: 'pointer' }}
                      >
                        Change Password{' '}
                        <ChangePasswordIcon
                          style={{ verticalAlign: 'middle', marginLeft: 4 }}
                        />
                      </div>
                    </div>
                  </>
                )}
                {/* Mobile: Show user info in profile card */}
                {isMobile && (
                  <div className={styles.mobileUserInfo}>
                    <div className={styles.mobileUserInfoRow}>
                      {/* Left: Profile Section */}
                      <div className={styles.mobileProfileSection}>
                        <div className={styles.mobileProfileDetails}>
                          <div className={styles.mobileProfileName}>
                            {user.username.toUpperCase()}
                          </div>
                          <div className={styles.mobileProfileActions}>
                            <span
                              className={styles.mobileEditLink}
                              onClick={handleEditInfo}
                            >
                              Edit Info{' '}
                              <EditIcon
                                style={{
                                  verticalAlign: 'middle',
                                  marginLeft: 4,
                                }}
                              />
                            </span>
                            <span
                              className={styles.mobileEditLink}
                              onClick={() => setShowChangePassword(true)}
                            >
                              Change Password{' '}
                              <ChangePasswordIcon
                                style={{
                                  verticalAlign: 'middle',
                                  marginLeft: 4,
                                }}
                              />
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Right: User Details Section */}
                      <div className={styles.mobileDetailsSection}>
                        {/* <div className={styles.mobileDetailItem}> */}
                        {user?.distributorName ? (
                          <div className={styles.mobileDetailItem}>
                            <span className={styles.mobileDetailLabel}>
                              Distributor Name:
                            </span>
                            <span className={styles.mobileDetailValue}>
                              {user.distributorName}
                            </span>
                          </div>
                        ) : null}
                        {user?.subdistributorName ? (
                          <div className={styles.mobileDetailItem}>
                            <span className={styles.mobileDetailLabel}>
                              Sub Distributor Name:
                            </span>
                            <span className={styles.mobileDetailValue}>
                              {user.subdistributorName}
                            </span>
                          </div>
                        ) : null}
                        {user?.agentName ? (
                          <div className={styles.mobileDetailItem}>
                            <span className={styles.mobileDetailLabel}>
                              Agent Name:
                            </span>
                            <span className={styles.mobileDetailValue}>
                              {user.agentName}
                            </span>
                          </div>
                        ) : null}
                        {user?.shopName ? (
                          <div className={styles.mobileDetailItem}>
                            <span className={styles.mobileDetailLabel}>
                              Shop Name:
                            </span>
                            <span className={styles.mobileDetailValue}>
                              {user.shopName}
                            </span>
                          </div>
                        ) : null}
                        {user?.environment ? (
                          <div className={styles.mobileDetailItem}>
                            <span className={styles.mobileDetailLabel}>
                              Environment:
                            </span>
                            <span className={styles.mobileDetailValue}>
                              {getEnvironmentDisplayName(user.environment.name)}
                            </span>
                          </div>
                        ) : null}
                        {/* </div> */}
                        <div className={styles.mobileDetailItem}>
                          <span className={styles.mobileDetailLabel}>
                            Username:
                          </span>
                          <span className={styles.mobileDetailValue}>
                            {editInfoMode ? (
                              <input
                                className={styles.positionInput}
                                value={editUsername}
                                onChange={(e) =>
                                  setEditUsername(e.target.value)
                                }
                                placeholder="Username"
                                style={{
                                  marginLeft: 8,
                                  minWidth: 120,
                                  fontSize: '14px',
                                  padding: '4px 8px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                }}
                                disabled={editLoading}
                              />
                            ) : (
                              user.username
                            )}
                          </span>
                        </div>
                        <div className={styles.mobileDetailItem}>
                          <span className={styles.mobileDetailLabel}>
                            IP Address:
                          </span>
                          <span className={styles.mobileDetailValue}>
                            {user.loginIp}
                          </span>
                        </div>
                        <div className={styles.mobileDetailItem}>
                          <span className={styles.mobileDetailLabel}>
                            Status:
                          </span>
                          <span
                            className={`${styles.statusSwitch} ${user.isActive ? styles.active : ''
                              }`}
                            style={{
                              cursor: 'pointer',
                              opacity: statusLoading ? 0.6 : 1,
                            }}
                            onClick={
                              statusLoading ? undefined : handleToggleStatus
                            }
                            title="Click to toggle status"
                          >
                            <span className={styles.statusSwitchDot} />
                          </span>
                        </div>
                        {/* Mobile Edit Actions */}
                        {editInfoMode && (
                          <div className={styles.mobileEditActions}>
                            <button
                              className={styles.positionButton}
                              onClick={handleSaveEdit}
                              disabled={editLoading}
                              style={{
                                marginRight: 8,
                                fontSize: '12px',
                                padding: '6px 12px',
                              }}
                            >
                              {editLoading ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              className={`${styles.positionButton} secondary`}
                              onClick={handleCancelEdit}
                              disabled={editLoading}
                              style={{
                                fontSize: '12px',
                                padding: '6px 12px',
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                        {editError && (
                          <div
                            className={styles.error}
                            style={{ marginTop: 8 }}
                          >
                            {editError}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Desktop: Show profit card only on desktop */}
              {!isMobile && (
                <div className={styles.desktopProfitCard}>
                  <div className={styles.profitLabel}>Profit</div>
                  <div className={styles.profitValueBox}>
                    <div className={styles.profitIcon}>
                      <ProfitIcon />
                    </div>
                    <div className={styles.profitValue}>
                      {refetchingUser ? (
                        <span style={{ opacity: 0.6 }}>Refreshing...</span>
                      ) : (
                        `$${formatNumberWithCommas(childBalanceReport.totalProfit)}`
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Center: Balance, Info, Actions */}
            <div className={styles.centerCol}>
              {/* Desktop: Show info card only on desktop */}
              {!isMobile && (
                <div className={styles.infoCard}>
                  {/* <div
                    style={{
                      display: 'flex',
                      // alignItems: 'center',
                      flexDirection: 'column',
                      marginBottom: 8,
                      gap: 8,
                    }}
                  > */}
                  {user?.distributorName ? <div
                    style={{
                      display: 'flex',
                      // alignItems: 'center',
                      // flexDirection: 'column',
                      marginBottom: 8,
                      gap: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Distributor Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.distributorName}
                    </span>
                  </div> : null}
                  {user?.subdistributorName ? <div
                    style={{
                      display: 'flex',
                      // alignItems: 'center',
                      // flexDirection: 'column',
                      marginBottom: 8,
                      gap: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Subdistributor Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.subdistributorName}
                    </span>
                  </div> : null}
                  {user?.agentName ? <div
                    style={{
                      display: 'flex',
                      // alignItems: 'center',
                      // flexDirection: 'column',
                      marginBottom: 8,
                      gap: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Agent Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.agentName}
                    </span>
                  </div> : null}
                  {user?.shopName ? <div
                    style={{
                      display: 'flex',
                      // alignItems: 'center',
                      // flexDirection: 'column',
                      marginBottom: 8,
                      gap: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Shop Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.shopName}
                    </span>
                  </div> : null}
                  {user?.environment ? <div
                    style={{
                      display: 'flex',
                      // alignItems: 'center',
                      // flexDirection: 'column',
                      marginBottom: 8,
                      gap: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Environment:</b>
                    <span style={{ marginLeft: 8 }}>
                      {getEnvironmentDisplayName(user.environment.name)}
                    </span>
                  </div> : null}

                  {/* </div> */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}
                  >
                    <b style={{ minWidth: 130 }}>Username:</b>
                    {editInfoMode ? (
                      <input
                        className={styles.positionInput}
                        value={editUsername}
                        onChange={(e) => setEditUsername(e.target.value)}
                        placeholder="Username"
                        style={{ marginLeft: 8, minWidth: 120 }}
                        disabled={editLoading}
                      />
                    ) : (
                      <span style={{ marginLeft: 8 }}>{user.username}</span>
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}
                  >
                    <b style={{ minWidth: 130 }}>IP Address:</b>{' '}
                    <span style={{ marginLeft: 8 }}>{user.loginIp}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <b style={{ minWidth: 130 }}>Status:</b>{' '}
                    <span
                      className={`${styles.statusSwitch} ${user.isActive ? styles.active : ''
                        }`}
                      style={{
                        marginLeft: 8,
                        cursor: 'pointer',
                        opacity: statusLoading ? 0.6 : 1,
                      }}
                      onClick={statusLoading ? undefined : handleToggleStatus}
                      title="Click to toggle status"
                    >
                      <span className={styles.statusSwitchDot} />
                    </span>
                  </div>
                  {editInfoMode ? (
                    <div style={{ marginTop: 12 }}>
                      <button
                        className={styles.positionButton}
                        onClick={handleSaveEdit}
                        disabled={editLoading}
                        style={{ marginRight: 4 }}
                      >
                        {editLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className={`${styles.positionButton} secondary`}
                        onClick={handleCancelEdit}
                        disabled={editLoading}
                      >
                        Cancel
                      </button>
                      {editError && (
                        <div className={styles.error}>{editError}</div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
              {/* Desktop: Show balance card only on desktop */}
              <div className={styles.desktopBalanceCard + ' ' + (user.wagerBonus > 0 ? styles.hasBonus : '')}>
                {user.wagerBonus > 0 && (
                  <div className={styles.wagerBonusLabel}>Wager bonus:  ${user.wagerBonus}</div>
                )}
                <div className={styles.balanceLabel}>Total Balance</div>
                <div className={styles.balanceValue}>
                  {refetchingUser ? (
                    <span style={{ opacity: 0.6 }}>Refreshing...</span>
                  ) : user.wagerBonus > 0 ? (
                    `$${user.Balance && formatNumberWithCommas((user.Balance?.amount || user.Balance?.onHold) - user.wagerBonus)} + $${formatNumberWithCommas(user.wagerBonus)} = $${formatNumberWithCommas(user.Balance?.amount || 0)}`
                  ) : (
                    `$${formatNumberWithCommas(user.Balance?.amount || user.Balance?.onHold || 0)}`
                  )}
                </div>
                <div className={styles.balanceChangeRow}>
                  <span className={styles.increase}>▲ ${user.balanceIn}</span>
                  <span className={styles.decrease}>▼ ${user.balanceOut}</span>
                </div>
              </div>
            </div>

            {/* Right: User Activity - Hidden on mobile */}
            {!isMobile && (
              <div className={styles.rightCol}>
                <div className={styles.activityCard}>
                  <div className={styles.activityTitle}>User Activity</div>
                  <div
                    className={styles.activityList}
                    ref={activityListRef}
                    onScroll={handleUserActivityScroll}
                    style={{ position: 'relative' }}
                  >
                    {userActivityLoading ? (
                      <div>Loading...</div>
                    ) : userActivityError ? (
                      <div className={styles.error}>{userActivityError}</div>
                    ) : userActivity.length === 0 ? (
                      <div>No activity found</div>
                    ) : (
                      <>
                        {userActivity.map((a, i) => (
                          <div key={i} className={styles.activityItem}>
                            <div className={styles.activityTime}>
                              {formatLoginTime(a.date)}
                            </div>
                            <div className={styles.activityDesc}>
                              {a.activity}
                            </div>
                          </div>
                        ))}
                        {userActivityLoadingMore && (
                          <div style={{ textAlign: 'center', padding: 8 }}>
                            Loading more...
                          </div>
                        )}
                        {!userActivityHasNextPage &&
                          userActivity.length > 0 && (
                            <div
                              style={{
                                textAlign: 'center',
                                color: '#888',
                                fontSize: 13,
                                padding: 8,
                              }}
                            >
                              No more activity
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile: Combined Profit and Balance Row */}
          {isMobile && (
            <div className={styles.mobileProfitBalanceRow}>
              <div className={styles.mobileProfitCard}>
                <div className={styles.profitLabel}>Profit</div>
                <div className={styles.profitValueBox}>
                  <div className={styles.profitIcon}>
                    <ProfitIcon />
                  </div>
                  <div className={styles.profitValue}>
                    {refetchingUser ? (
                      <span style={{ opacity: 0.6 }}>Refreshing...</span>
                    ) : (
                      `$${formatNumberWithCommas(childBalanceReport.totalProfit)}`
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.mobileBalanceCard}>
                <div className={styles.balanceLabel}>Total Balance</div>
                <div className={styles.balanceValue}>
                  {refetchingUser ? (
                    <span style={{ opacity: 0.6 }}>Refreshing...</span>
                  ) : (
                    `$${formatNumberWithCommas(user.Balance?.amount || user.Balance?.onHold || 0)}`
                  )}
                </div>
                <div className={styles.balanceChangeRow}>
                  <span className={styles.increase}>▲ ${user.balanceIn}</span>
                  <span className={styles.decrease}>▼ ${user.balanceOut}</span>
                </div>
              </div>
            </div>
          )}

          <div className={styles.cashRow}>
            <div className={styles.inOutCard}>
              <div className={styles.inOutIcon}>
                <InIcon />
              </div>
              <div className={styles.inOutLabel}>IN</div>
              <div className={styles.inOutValue}>
                {refetchingUser ? (
                  <span style={{ opacity: 0.6 }}>Refreshing...</span>
                ) : (
                  `$${formatNumberWithCommas(user.balanceIn)}`
                )}
              </div>
            </div>
            <div className={styles.cashActionsCol}>
              <button
                className={styles.cashInBtn}
                onClick={() => setModalType('in')}
              >
                Cash In +
              </button>
              <button
                className={styles.cashOutBtn}
                onClick={() => setModalType('out')}
              >
                Cash Out -
              </button>
            </div>
            <div className={styles.inOutCard}>
              <div className={styles.inOutIcon}>
                <OutIcon />
              </div>
              <div className={styles.inOutLabel}>OUT</div>
              <div className={styles.inOutValue}>
                {refetchingUser ? (
                  <span style={{ opacity: 0.6 }}>Refreshing...</span>
                ) : (
                  `$${formatNumberWithCommas(user.balanceOut)}`
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {activeSubTab === 'transactions' && (
        // Transactions View
        <>
          {/* User Info and Cash Actions Row */}
          {!isMobile ? (
            <div className={styles.transactionsHeader}>
              <div className={styles.userInfoSection}>
                <div className={styles.profileEditLinks}>
                  <div className={styles.profileName}>
                    {user.username.toUpperCase()}
                  </div>
                  <div
                    className={styles.profileLink}
                    onClick={handleEditInfo}
                    style={{ cursor: 'pointer' }}
                  >
                    Edit Info{' '}
                    <EditIcon
                      style={{ verticalAlign: 'middle', marginLeft: 4 }}
                    />
                  </div>
                  <div
                    className={styles.profileLink}
                    onClick={() => setShowChangePassword(true)}
                    style={{ cursor: 'pointer' }}
                  >
                    Change Password{' '}
                    <ChangePasswordIcon
                      style={{ verticalAlign: 'middle', marginLeft: 4 }}
                    />
                  </div>
                </div>
                <div className={styles.userInfoCard}>
                  {/* <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        // alignItems: 'center',
                        flexDirection: 'column',
                        marginBottom: 8,
                        gap: 8,
                      }}
                    > */}
                  {user?.distributorName ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Distributor Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.distributorName}
                    </span>
                  </div> : null}
                  {user?.subdistributorName ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Subdistributor Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.subdistributorName}
                    </span>
                  </div> : null}
                  {user?.agentName ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Agent Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.agentName}
                    </span>
                  </div> : null}
                  {user?.shopName ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Shop Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.shopName}
                    </span>
                  </div> : null}
                  {user?.environment ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Environment:</b>
                    <span style={{ marginLeft: 8 }}>
                      {getEnvironmentDisplayName(user.environment.name)}
                    </span>
                  </div> : null}

                  {/* </div> */}
                  {/* </div> */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}
                  >
                    <b style={{ minWidth: 130 }}>Username:</b>
                    {editInfoMode ? (
                      <input
                        className={styles.positionInput}
                        value={editUsername}
                        onChange={(e) => setEditUsername(e.target.value)}
                        placeholder="Username"
                        style={{ marginLeft: 8, minWidth: 120 }}
                        disabled={editLoading}
                      />
                    ) : (
                      <span style={{ marginLeft: 8 }}>{user.username}</span>
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}
                  >
                    <b style={{ minWidth: 130 }}>IP Address:</b>{' '}
                    <span style={{ marginLeft: 8 }}>{user.loginIp}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <b style={{ minWidth: 130 }}>Status:</b>{' '}
                    <span
                      className={`${styles.statusSwitch} ${user.isActive ? styles.active : ''
                        }`}
                      style={{
                        marginLeft: 8,
                        cursor: 'pointer',
                        opacity: statusLoading ? 0.6 : 1,
                      }}
                      onClick={statusLoading ? undefined : handleToggleStatus}
                      title="Click to toggle status"
                    >
                      <span className={styles.statusSwitchDot} />
                    </span>
                  </div>
                  {editInfoMode ? (
                    <div style={{ marginTop: 12 }}>
                      <button
                        className={styles.positionButton}
                        onClick={handleSaveEdit}
                        disabled={editLoading}
                        style={{ marginRight: 4 }}
                      >
                        {editLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className={`${styles.positionButton} secondary`}
                        onClick={handleCancelEdit}
                        disabled={editLoading}
                      >
                        Cancel
                      </button>
                      {editError && (
                        <div className={styles.error}>{editError}</div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className={styles.cashActionsSection}>
                <button
                  className={styles.cashInBtn}
                  onClick={() => setModalType('in')}
                >
                  Cash In +
                </button>
                <button
                  className={styles.cashOutBtn}
                  onClick={() => setModalType('out')}
                >
                  Cash Out -
                </button>
                {cashOperationError && (
                  <div
                    className={styles.error}
                    style={{ marginTop: '8px', color: 'red' }}
                  >
                    {cashOperationError}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Mobile Transactions Header
            <div className={styles.mobileTransactionsHeader}>
              <div className={styles.mobileTransactionsUserInfo}>
                <div className={styles.mobileTransactionsUserHeader}>
                  <div className={styles.mobileTransactionsUserName}>
                    {user.username.toUpperCase()}
                  </div>
                  <div className={styles.mobileTransactionsUserActions}>
                    <span
                      className={styles.mobileTransactionsUserAction}
                      onClick={handleEditInfo}
                    >
                      Edit Info{' '}
                      <EditIcon
                        style={{
                          verticalAlign: 'middle',
                          marginLeft: 4,
                        }}
                      />
                    </span>
                    <span
                      className={styles.mobileTransactionsUserAction}
                      onClick={() => setShowChangePassword(true)}
                    >
                      Change Password{' '}
                      <ChangePasswordIcon
                        style={{
                          verticalAlign: 'middle',
                          marginLeft: 4,
                        }}
                      />
                    </span>
                  </div>
                </div>
                <div className={styles.mobileTransactionsUserDetails}>
                  {/* <div className={styles.mobileTransactionsDetailItem}> */}
                  {user?.distributorName ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Distributor Name:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {user.distributorName}
                      </span>
                    </div>
                  ) : null}
                  {user?.subdistributorName ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Sub Distributor Name:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {user.subdistributorName}
                      </span>
                    </div>
                  ) : null}
                  {user?.agentName ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Agent Name:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {user.agentName}
                      </span>
                    </div>
                  ) : null}
                  {user?.shopName ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Shop Name:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {user.shopName}
                      </span>
                    </div>
                  ) : null}
                  {user?.environment ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Environment:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {getEnvironmentDisplayName(user.environment.name)}
                      </span>
                    </div>
                  ) : null}
                  {/* </div> */}
                  <div className={styles.mobileTransactionsDetailItem}>
                    <span className={styles.mobileTransactionsDetailLabel}>
                      Username:
                    </span>
                    <span className={styles.mobileTransactionsDetailValue}>
                      {editInfoMode ? (
                        <input
                          className={styles.positionInput}
                          value={editUsername}
                          onChange={(e) => setEditUsername(e.target.value)}
                          placeholder="Username"
                          style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                          }}
                          disabled={editLoading}
                        />
                      ) : (
                        user.username
                      )}
                    </span>
                  </div>
                  <div className={styles.mobileTransactionsDetailItem}>
                    <span className={styles.mobileTransactionsDetailLabel}>
                      IP Address:
                    </span>
                    <span className={styles.mobileTransactionsDetailValue}>
                      {user.loginIp}
                    </span>
                  </div>
                  <div className={styles.mobileTransactionsDetailItem}>
                    <span className={styles.mobileTransactionsDetailLabel}>
                      Status:
                    </span>
                    <span
                      className={`${styles.statusSwitch} ${user.isActive ? styles.active : ''
                        }`}
                      style={{
                        cursor: 'pointer',
                        opacity: statusLoading ? 0.6 : 1,
                      }}
                      onClick={statusLoading ? undefined : handleToggleStatus}
                      title="Click to toggle status"
                    >
                      <span className={styles.statusSwitchDot} />
                    </span>
                  </div>
                  {editInfoMode && (
                    <div className={styles.mobileEditActions}>
                      <button
                        className={styles.positionButton}
                        onClick={handleSaveEdit}
                        disabled={editLoading}
                        style={{
                          marginRight: 8,
                          fontSize: '12px',
                          padding: '6px 12px',
                        }}
                      >
                        {editLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className={`${styles.positionButton} secondary`}
                        onClick={handleCancelEdit}
                        disabled={editLoading}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {editError && (
                    <div className={styles.error} style={{ marginTop: 8 }}>
                      {editError}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.mobileTransactionsCashActions}>
                <button
                  className={`${styles.mobileTransactionsCashBtn} ${styles.mobileTransactionsCashInBtn}`}
                  onClick={() => setModalType('in')}
                >
                  Cash In +
                </button>
                <button
                  className={`${styles.mobileTransactionsCashBtn} ${styles.mobileTransactionsCashOutBtn}`}
                  onClick={() => setModalType('out')}
                >
                  Cash Out -
                </button>
              </div>
              {cashOperationError && (
                <div
                  className={styles.error}
                  style={{ marginTop: '8px', color: 'red' }}
                >
                  {cashOperationError}
                </div>
              )}
            </div>
          )}

          {/* Transactions Header and Search */}
          {!isMobile ? (
            <div className={styles.transactionsHeaderRow}>
              <h2 className={styles.transactionsTitle}>Transactions</h2>
              <div className={styles.searchByDateRow}>
                <span className={styles.searchByDateLabel}>Search by Date</span>
                <div className={styles.dateSearchBox}>
                  <span className={styles.dateIcon}>📅</span>
                  <input
                    type="date"
                    className={styles.dateInput}
                    placeholder="Start Date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                  <input
                    type="time"
                    className={styles.timeInput}
                    value={timeFrom}
                    onChange={(e) => setTimeFrom(e.target.value)}
                    placeholder="Start Time"
                  />
                  <span className={styles.toLabel}>To</span>
                  <input
                    type="date"
                    className={styles.dateInput}
                    placeholder="End Date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                  <input
                    type="time"
                    className={styles.timeInput}
                    value={timeTo}
                    onChange={(e) => setTimeTo(e.target.value)}
                    placeholder="Finish Time"
                  />
                </div>
                <button onClick={handleSearch} className={styles.searchButton}>Search</button>
              </div>
            </div>
          ) : (
            // Mobile Search Section
            <div className={styles.mobileSearchSection}>
              <div className={styles.mobileSearchTitle}>Search by Date</div>
              <div className={styles.mobileSearchForm}>
                <div className={styles.mobileSearchRow}>
                  <span className={styles.mobileSearchLabel}>Start Date</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className={styles.mobileSearchInput}
                    placeholder="Start Date"
                  />
                </div>
                <div className={styles.mobileSearchRow}>
                  <span className={styles.mobileSearchLabel}>End Date</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className={styles.mobileSearchInput}
                    placeholder="End Date"
                  />
                </div>
                <button onClick={handleSearch} className={styles.mobileSearchButton}>Search</button>
              </div>
            </div>
          )}

          {/* Transactions Table/Cards */}
          <TableLoading
            loading={balanceHistoryLoading}
            rows={8}
            columns={7}
            text="Loading transactions..."
            className={styles.tableWrapper}
          >
            {!isMobile ? (
              // Desktop Table View
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Wager</th>
                      <th>Type</th>
                      <th>User</th>
                      <th>IP Address</th>
                      <th>Comments</th>
                      <th>Added By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balanceHistory.length > 0 ? (
                      <>
                        {balanceHistory.map((transaction, i) => (
                          <tr key={i}>
                            <td data-label="Date">
                              {formatLoginTime(transaction.createdAt)}
                            </td>
                            <td
                              className={
                                transaction.type === 'cashIn'
                                  ? styles.inCol
                                  : styles.outCol
                              }
                              data-label="Amount"
                            >
                              {transaction.type === 'cashIn' ? '+' : '-'}$
                              {formatNumberWithCommas(
                                Math.abs(transaction.amount),
                              )}
                            </td>
                            <td
                              className={
                                transaction.type === 'cashIn'
                                  ? styles.inCol
                                  : styles.outCol
                              }
                              data-label="Wager">
                              {transaction?.parentBalanceHistory &&
                                (transaction.type === 'cashIn' ? '+' : '-') +
                                '$' +
                                formatNumberWithCommas(
                                  Math.abs(
                                    transaction?.parentBalanceHistory?.change,
                                  ),
                                )}
                            </td>
                            <td data-label="Type">{transaction.type}</td>
                            <td data-label="User">{transaction.user}</td>
                            <td data-label="IP Address">
                              {transaction.ipAddress || '-'}
                            </td>
                            <td data-label="Comments">
                              {transaction.comments || '-'}
                            </td>
                            <td data-label="Added By">
                              {transaction.addedBy || '-'}
                            </td>

                          </tr>
                        ))}
                      </>

                    ) : (
                      <tr>
                        <td colSpan={8} style={{ textAlign: 'center' }}>
                          No transactions found
                        </td>
                      </tr>
                    )}

                  </tbody>
                </table>
                {/* Pagination controls */}
                <div className={styles.pagination}>
                  {(transactionsTotal && Math.ceil(transactionsTotal / TRANSACTIONS_PAGE_SIZE) > 0) ?
                    <Stack spacing={1}>
                      <Pagination
                        onChange={(e, value) => setTransactionsPage(value)}
                        renderItem={(item) => (
                          <PaginationItem
                            {...item}
                            slots={{
                              previous: () => (
                                <button
                                  onClick={() => {
                                    if (transactionsPage > 1) setTransactionsPage((p) => p - 1);
                                  }}
                                  disabled={transactionsPage === 1}
                                  className={styles.searchBtn + " " + styles.paginationBtn}
                                >
                                  &lt;
                                </button>
                              ),
                              next: () => (
                                <button
                                  onClick={() => {
                                    if (transactionsPage < Math.ceil(transactionsTotal / TRANSACTIONS_PAGE_SIZE)) setTransactionsPage((p) => p + 1);
                                  }}
                                  disabled={transactionsPage >= Math.ceil(transactionsTotal / TRANSACTIONS_PAGE_SIZE)}
                                  className={styles.searchBtn + " " + styles.paginationBtn}
                                >
                                  &gt;
                                </button>
                              ),
                            }}
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: "#313485",
                                color: "#fff",
                                padding: "5px",
                                "&:hover": { backgroundColor: "#232665" }, // darker hover
                              },
                              // "&:not(.Mui-selected)": {
                              //   border: "1px solid #313485",
                              //   color: "#313485",
                              // },
                              // borderRadius: "8px", // optional rounding
                              padding: "5px",
                            }}
                          />
                        )}
                        count={Math.ceil(transactionsTotal / TRANSACTIONS_PAGE_SIZE)}
                        page={transactionsPage}
                        color="primary"
                        size="medium"
                        shape="rounded"
                        siblingCount={siblingCount}
                        boundaryCount={1}
                      />
                    </Stack>
                    :
                    <button
                      disabled={transactionsPage === 1}
                      className={styles.searchBtn + ' ' + styles.paginationBtn}
                    >
                      1
                    </button>
                  }
                </div>
              </div>
            ) : (
              // Mobile Table View (responsive table like distributor page)
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Type</th>
                      <th>User</th>
                      <th>IP Address</th>
                      <th>Comments</th>
                      <th>Added By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balanceHistory.length > 0 ? (
                      balanceHistory.map((transaction, i) => (
                        <tr key={i}>
                          <td data-label="Date">
                            {formatLoginTime(transaction.createdAt)}
                          </td>
                          <td
                            className={
                              transaction.type === 'cashIn'
                                ? styles.inCol
                                : styles.outCol
                            }
                            data-label="Amount"
                          >
                            {transaction.type === 'cashIn' ? '+' : '-'}$
                            {formatNumberWithCommas(
                              Math.abs(transaction.amount),
                            )}
                          </td>
                          <td data-label="Type">{transaction.type}</td>
                          <td data-label="User">{transaction.user}</td>
                          <td data-label="IP Address">
                            {transaction.ipAddress || '-'}
                          </td>
                          <td data-label="Comments">
                            {transaction.comments || '-'}
                          </td>
                          <td data-label="Added By">
                            {transaction.addedBy || '-'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center' }}>
                          No transactions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {/* Mobile Pagination */}
                <div className={styles.pagination}>
                  <button
                    onClick={() => {
                      if (transactionsPage > 1)
                        setTransactionsPage((p) => p - 1);
                    }}
                    disabled={transactionsPage === 1}
                    className={styles.searchButton}
                  >
                    &lt;
                  </button>
                  <span>
                    Page {transactionsPage} of{' '}
                    {Math.ceil(transactionsTotal / TRANSACTIONS_PAGE_SIZE) || 1}
                  </span>
                  <button
                    onClick={() => {
                      if (
                        transactionsPage <
                        Math.ceil(transactionsTotal / TRANSACTIONS_PAGE_SIZE)
                      ) {
                        setTransactionsPage((p) => p + 1);
                      }
                    }}
                    disabled={
                      transactionsPage >=
                      Math.ceil(transactionsTotal / TRANSACTIONS_PAGE_SIZE)
                    }
                    className={styles.searchButton}
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}
          </TableLoading>
          {/* Totals for the searched range (cash / online / total) */}
          {renderBalanceReportTable()}
        </>
      )}

      {activeSubTab === 'onlineTransactions' && (
        // Online Transactions View
        <>
          {/* User Info and Cash Actions Row */}
          {!isMobile ? (
            <div className={styles.transactionsHeader}>
              <div className={styles.userInfoSection}>
                <div className={styles.profileEditLinks}>
                  <div className={styles.profileName}>
                    {user.username.toUpperCase()}
                  </div>
                  <div
                    className={styles.profileLink}
                    onClick={handleEditInfo}
                    style={{ cursor: 'pointer' }}
                  >
                    Edit Info{' '}
                    <EditIcon
                      style={{ verticalAlign: 'middle', marginLeft: 4 }}
                    />
                  </div>
                  <div
                    className={styles.profileLink}
                    onClick={() => setShowChangePassword(true)}
                    style={{ cursor: 'pointer' }}
                  >
                    Change Password{' '}
                    <ChangePasswordIcon
                      style={{ verticalAlign: 'middle', marginLeft: 4 }}
                    />
                  </div>
                </div>
                <div className={styles.userInfoCard}>
                  {user?.distributorName ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Distributor Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.distributorName}
                    </span>
                  </div> : null}
                  {user?.subdistributorName ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Subdistributor Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.subdistributorName}
                    </span>
                  </div> : null}
                  {user?.agentName ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Agent Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.agentName}
                    </span>
                  </div> : null}
                  {user?.shopName ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Shop Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.shopName}
                    </span>
                  </div> : null}
                  {user?.environment ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Environment:</b>
                    <span style={{ marginLeft: 8 }}>
                      {getEnvironmentDisplayName(user.environment.name)}
                    </span>
                  </div> : null}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}
                  >
                    <b style={{ minWidth: 130 }}>Username:</b>
                    {editInfoMode ? (
                      <input
                        className={styles.positionInput}
                        value={editUsername}
                        onChange={(e) => setEditUsername(e.target.value)}
                        placeholder="Username"
                        style={{ marginLeft: 8, minWidth: 120 }}
                        disabled={editLoading}
                      />
                    ) : (
                      <span style={{ marginLeft: 8 }}>{user.username}</span>
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}
                  >
                    <b style={{ minWidth: 130 }}>IP Address:</b>{' '}
                    <span style={{ marginLeft: 8 }}>{user.loginIp}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <b style={{ minWidth: 130 }}>Status:</b>{' '}
                    <span
                      className={`${styles.statusSwitch} ${user.isActive ? styles.active : ''
                        }`}
                      style={{
                        marginLeft: 8,
                        cursor: 'pointer',
                        opacity: statusLoading ? 0.6 : 1,
                      }}
                      onClick={statusLoading ? undefined : handleToggleStatus}
                      title="Click to toggle status"
                    >
                      <span className={styles.statusSwitchDot} />
                    </span>
                  </div>
                  {editInfoMode ? (
                    <div style={{ marginTop: 12 }}>
                      <button
                        className={styles.positionButton}
                        onClick={handleSaveEdit}
                        disabled={editLoading}
                        style={{ marginRight: 4 }}
                      >
                        {editLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className={`${styles.positionButton} secondary`}
                        onClick={handleCancelEdit}
                        disabled={editLoading}
                      >
                        Cancel
                      </button>
                      {editError && (
                        <div className={styles.error}>{editError}</div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className={styles.cashActionsSection}>
                <button
                  className={styles.cashInBtn}
                  onClick={() => setModalType('in')}
                >
                  Cash In +
                </button>
                <button
                  className={styles.cashOutBtn}
                  onClick={() => setModalType('out')}
                >
                  Cash Out -
                </button>
                {cashOperationError && (
                  <div
                    className={styles.error}
                    style={{ marginTop: '8px', color: 'red' }}
                  >
                    {cashOperationError}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Mobile Online Transactions Header
            <div className={styles.mobileTransactionsHeader}>
              <div className={styles.mobileTransactionsUserInfo}>
                <div className={styles.mobileTransactionsUserHeader}>
                  <div className={styles.mobileTransactionsUserName}>
                    {user.username.toUpperCase()}
                  </div>
                  <div className={styles.mobileTransactionsUserActions}>
                    <span
                      className={styles.mobileTransactionsUserAction}
                      onClick={handleEditInfo}
                    >
                      Edit Info{' '}
                      <EditIcon
                        style={{
                          verticalAlign: 'middle',
                          marginLeft: 4,
                        }}
                      />
                    </span>
                    <span
                      className={styles.mobileTransactionsUserAction}
                      onClick={() => setShowChangePassword(true)}
                    >
                      Change Password{' '}
                      <ChangePasswordIcon
                        style={{
                          verticalAlign: 'middle',
                          marginLeft: 4,
                        }}
                      />
                    </span>
                  </div>
                </div>
                <div className={styles.mobileTransactionsUserDetails}>
                  {user?.distributorName ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Distributor Name:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {user.distributorName}
                      </span>
                    </div>
                  ) : null}
                  {user?.subdistributorName ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Sub Distributor Name:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {user.subdistributorName}
                      </span>
                    </div>
                  ) : null}
                  {user?.agentName ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Agent Name:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {user.agentName}
                      </span>
                    </div>
                  ) : null}
                  {user?.shopName ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Shop Name:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {user.shopName}
                      </span>
                    </div>
                  ) : null}
                  {user?.environment ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Environment:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {getEnvironmentDisplayName(user.environment.name)}
                      </span>
                    </div>
                  ) : null}
                  <div className={styles.mobileTransactionsDetailItem}>
                    <span className={styles.mobileTransactionsDetailLabel}>
                      Username:
                    </span>
                    <span className={styles.mobileTransactionsDetailValue}>
                      {editInfoMode ? (
                        <input
                          className={styles.positionInput}
                          value={editUsername}
                          onChange={(e) => setEditUsername(e.target.value)}
                          placeholder="Username"
                          style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                          }}
                          disabled={editLoading}
                        />
                      ) : (
                        user.username
                      )}
                    </span>
                  </div>
                  <div className={styles.mobileTransactionsDetailItem}>
                    <span className={styles.mobileTransactionsDetailLabel}>
                      IP Address:
                    </span>
                    <span className={styles.mobileTransactionsDetailValue}>
                      {user.loginIp}
                    </span>
                  </div>
                  <div className={styles.mobileTransactionsDetailItem}>
                    <span className={styles.mobileTransactionsDetailLabel}>
                      Status:
                    </span>
                    <span
                      className={`${styles.statusSwitch} ${user.isActive ? styles.active : ''
                        }`}
                      style={{
                        cursor: 'pointer',
                        opacity: statusLoading ? 0.6 : 1,
                      }}
                      onClick={statusLoading ? undefined : handleToggleStatus}
                      title="Click to toggle status"
                    >
                      <span className={styles.statusSwitchDot} />
                    </span>
                  </div>
                  {editInfoMode && (
                    <div className={styles.mobileEditActions}>
                      <button
                        className={styles.positionButton}
                        onClick={handleSaveEdit}
                        disabled={editLoading}
                        style={{
                          marginRight: 8,
                          fontSize: '12px',
                          padding: '6px 12px',
                        }}
                      >
                        {editLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className={`${styles.positionButton} secondary`}
                        onClick={handleCancelEdit}
                        disabled={editLoading}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {editError && (
                    <div className={styles.error} style={{ marginTop: 8 }}>
                      {editError}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.mobileTransactionsCashActions}>
                <button
                  className={`${styles.mobileTransactionsCashBtn} ${styles.mobileTransactionsCashInBtn}`}
                  onClick={() => setModalType('in')}
                >
                  Cash In +
                </button>
                <button
                  className={`${styles.mobileTransactionsCashBtn} ${styles.mobileTransactionsCashOutBtn}`}
                  onClick={() => setModalType('out')}
                >
                  Cash Out -
                </button>
              </div>
              {cashOperationError && (
                <div
                  className={styles.error}
                  style={{ marginTop: '8px', color: 'red' }}
                >
                  {cashOperationError}
                </div>
              )}
            </div>
          )}

          {/* Online Transactions Header and Search */}
          {!isMobile ? (
            <div className={styles.transactionsHeaderRow}>
              <h2 className={styles.transactionsTitle}>Online Transactions</h2>
              <div className={styles.searchByDateRow}>
                <span className={styles.searchByDateLabel}>Search by Date</span>
                <div className={styles.dateSearchBox}>
                  <span className={styles.dateIcon}>📅</span>
                  <input
                    type="date"
                    className={styles.dateInput}
                    placeholder="Start Date"
                    value={onlineDateFrom}
                    onChange={(e) => setOnlineDateFrom(e.target.value)}
                  />
                  <input
                    type="time"
                    className={styles.timeInput}
                    value={onlineTimeFrom}
                    onChange={(e) => setOnlineTimeFrom(e.target.value)}
                    placeholder="Start Time"
                  />
                  <span className={styles.toLabel}>To</span>
                  <input
                    type="date"
                    className={styles.dateInput}
                    placeholder="End Date"
                    value={onlineDateTo}
                    onChange={(e) => setOnlineDateTo(e.target.value)}
                  />
                  <input
                    type="time"
                    className={styles.timeInput}
                    value={onlineTimeTo}
                    onChange={(e) => setOnlineTimeTo(e.target.value)}
                    placeholder="Finish Time"
                  />
                </div>
                <button onClick={handleOnlineSearch} className={styles.searchButton}>Search</button>
              </div>
            </div>
          ) : (
            // Mobile Search Section
            <div className={styles.mobileSearchSection}>
              <div className={styles.mobileSearchTitle}>Search by Date</div>
              <div className={styles.mobileSearchForm}>
                <div className={styles.mobileSearchRow}>
                  <span className={styles.mobileSearchLabel}>Start Date</span>
                  <input
                    type="date"
                    value={onlineDateFrom}
                    onChange={(e) => setOnlineDateFrom(e.target.value)}
                    className={styles.mobileSearchInput}
                    placeholder="Start Date"
                  />
                </div>
                <div className={styles.mobileSearchRow}>
                  <span className={styles.mobileSearchLabel}>End Date</span>
                  <input
                    type="date"
                    value={onlineDateTo}
                    onChange={(e) => setOnlineDateTo(e.target.value)}
                    className={styles.mobileSearchInput}
                    placeholder="End Date"
                  />
                </div>
                <button onClick={handleOnlineSearch} className={styles.mobileSearchButton}>Search</button>
              </div>
            </div>
          )}

          {/* Online Transactions Table/Cards */}
          <TableLoading
            loading={onlineTransactionsLoading}
            rows={8}
            columns={7}
            text="Loading online transactions..."
            className={styles.tableWrapper}
          >
            {!isMobile ? (
              // Desktop Table View
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Wager</th>
                      <th>Type</th>
                      <th>User</th>
                      <th>IP Address</th>
                      <th>Comments</th>
                      <th>Added By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onlineTransactions.length > 0 ? (
                      <>
                        {onlineTransactions.map((transaction, i) => (
                          <tr key={i}>
                            <td data-label="Date">
                              {formatLoginTime(transaction.createdAt)}
                            </td>
                            <td
                              className={
                                transaction.type === 'cashIn'
                                  ? styles.inCol
                                  : styles.outCol
                              }
                              data-label="Amount"
                            >
                              {transaction.type === 'cashIn' ? '+' : '-'}$
                              {formatNumberWithCommas(
                                Math.abs(transaction.amount),
                              )}
                            </td>
                            <td
                              className={
                                transaction.type === 'cashIn'
                                  ? styles.inCol
                                  : styles.outCol
                              }
                              data-label="Wager">
                              {transaction?.parentBalanceHistory &&
                                (transaction.type === 'cashIn' ? '+' : '-') +
                                '$' +
                                formatNumberWithCommas(
                                  Math.abs(
                                    transaction?.parentBalanceHistory?.change,
                                  ),
                                )}
                            </td>
                            <td data-label="Type">{transaction.type}</td>
                            <td data-label="User">{transaction.user}</td>
                            <td data-label="IP Address">
                              {transaction.ipAddress || '-'}
                            </td>
                            <td data-label="Comments">
                              {transaction.comments || '-'}
                            </td>
                            <td data-label="Added By">
                              {transaction.addedBy || '-'}
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td colSpan={8} style={{ textAlign: 'center' }}>
                          No online transactions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {/* Pagination controls */}
                <div className={styles.pagination}>
                  {(onlineTransactionsTotal && Math.ceil(onlineTransactionsTotal / ONLINE_TRANSACTIONS_PAGE_SIZE) > 0) ?
                    <Stack spacing={1}>
                      <Pagination
                        onChange={(e, value) => setOnlineTransactionsPage(value)}
                        renderItem={(item) => (
                          <PaginationItem
                            {...item}
                            slots={{
                              previous: () => (
                                <button
                                  onClick={() => {
                                    if (onlineTransactionsPage > 1) setOnlineTransactionsPage((p) => p - 1);
                                  }}
                                  disabled={onlineTransactionsPage === 1}
                                  className={styles.searchBtn + " " + styles.paginationBtn}
                                >
                                  &lt;
                                </button>
                              ),
                              next: () => (
                                <button
                                  onClick={() => {
                                    if (onlineTransactionsPage < Math.ceil(onlineTransactionsTotal / ONLINE_TRANSACTIONS_PAGE_SIZE)) setOnlineTransactionsPage((p) => p + 1);
                                  }}
                                  disabled={onlineTransactionsPage >= Math.ceil(onlineTransactionsTotal / ONLINE_TRANSACTIONS_PAGE_SIZE)}
                                  className={styles.searchBtn + " " + styles.paginationBtn}
                                >
                                  &gt;
                                </button>
                              ),
                            }}
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: "#313485",
                                color: "#fff",
                                padding: "5px",
                                "&:hover": { backgroundColor: "#232665" },
                              },
                              padding: "5px",
                            }}
                          />
                        )}
                        count={Math.ceil(onlineTransactionsTotal / ONLINE_TRANSACTIONS_PAGE_SIZE)}
                        page={onlineTransactionsPage}
                        color="primary"
                        size="medium"
                        shape="rounded"
                        siblingCount={siblingCount}
                        boundaryCount={1}
                      />
                    </Stack>
                    :
                    <button
                      disabled={onlineTransactionsPage === 1}
                      className={styles.searchBtn + ' ' + styles.paginationBtn}
                    >
                      1
                    </button>
                  }
                </div>
              </div>
            ) : (
              // Mobile Table View
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Type</th>
                      <th>User</th>
                      <th>IP Address</th>
                      <th>Comments</th>
                      <th>Added By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onlineTransactions.length > 0 ? (
                      onlineTransactions.map((transaction, i) => (
                        <tr key={i}>
                          <td data-label="Date">
                            {formatLoginTime(transaction.createdAt)}
                          </td>
                          <td
                            className={
                              transaction.type === 'cashIn'
                                ? styles.inCol
                                : styles.outCol
                            }
                            data-label="Amount"
                          >
                            {transaction.type === 'cashIn' ? '+' : '-'}$
                            {formatNumberWithCommas(
                              Math.abs(transaction.amount),
                            )}
                          </td>
                          <td data-label="Type">{transaction.type}</td>
                          <td data-label="User">{transaction.user}</td>
                          <td data-label="IP Address">
                            {transaction.ipAddress || '-'}
                          </td>
                          <td data-label="Comments">
                            {transaction.comments || '-'}
                          </td>
                          <td data-label="Added By">
                            {transaction.addedBy || '-'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center' }}>
                          No online transactions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {/* Mobile Pagination */}
                <div className={styles.pagination}>
                  <button
                    onClick={() => {
                      if (onlineTransactionsPage > 1)
                        setOnlineTransactionsPage((p) => p - 1);
                    }}
                    disabled={onlineTransactionsPage === 1}
                    className={styles.searchButton}
                  >
                    &lt;
                  </button>
                  <span>
                    Page {onlineTransactionsPage} of{' '}
                    {Math.ceil(onlineTransactionsTotal / ONLINE_TRANSACTIONS_PAGE_SIZE) || 1}
                  </span>
                  <button
                    onClick={() => {
                      if (
                        onlineTransactionsPage <
                        Math.ceil(onlineTransactionsTotal / ONLINE_TRANSACTIONS_PAGE_SIZE)
                      ) {
                        setOnlineTransactionsPage((p) => p + 1);
                      }
                    }}
                    disabled={
                      onlineTransactionsPage >=
                      Math.ceil(onlineTransactionsTotal / ONLINE_TRANSACTIONS_PAGE_SIZE)
                    }
                    className={styles.searchButton}
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}
          </TableLoading>
          {/* Totals for the searched range (cash / online / total) */}
          {renderBalanceReportTable()}
        </>
      )}

      {activeSubTab === 'subCustomers' && (
        // Sub Customers View
        <>
          {/* User Info and Cash Actions Row */}
          {!isMobile ? (
            <div className={styles.transactionsHeader}>
              <div className={styles.userInfoSection}>
                <div className={styles.profileEditLinks}>
                  <div className={styles.profileName}>
                    {user.username.toUpperCase()}
                  </div>
                  <div
                    className={styles.profileLink}
                    onClick={handleEditInfo}
                    style={{ cursor: 'pointer' }}
                  >
                    Edit Info{' '}
                    <EditIcon
                      style={{ verticalAlign: 'middle', marginLeft: 4 }}
                    />
                  </div>
                  <div
                    className={styles.profileLink}
                    onClick={() => setShowChangePassword(true)}
                    style={{ cursor: 'pointer' }}
                  >
                    Change Password{' '}
                    <ChangePasswordIcon
                      style={{ verticalAlign: 'middle', marginLeft: 4 }}
                    />
                  </div>
                </div>
                <div className={styles.userInfoCard}>
                  {/* <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                      // gap: 8,
                    }}
                  > */}
                  {/* <div
                      style={{
                        display: 'flex',
                        // alignItems: 'center',
                        flexDirection: 'column',
                        marginBottom: 8,
                        gap: 8,
                      }}
                    > */}
                  {user?.distributorName ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Distributor Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.distributorName}
                    </span>
                  </div> : null}
                  {user?.subdistributorName ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Subdistributor Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.subdistributorName}
                    </span>
                  </div> : null}
                  {user?.agentName ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Agent Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.agentName}
                    </span>
                  </div> : null}
                  {user?.shopName ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Shop Name:</b>
                    <span style={{ marginLeft: 8 }}>
                      {user.shopName}
                    </span>
                  </div> : null}
                  {user?.environment ? <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}>
                    <b style={{ minWidth: 130 }}>Environment:</b>
                    <span style={{ marginLeft: 8 }}>
                      {getEnvironmentDisplayName(user.environment.name)}
                    </span>
                  </div> : null}

                  {/* </div> */}
                  {/* </div> */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}
                  >
                    <b style={{ minWidth: 130 }}>Username:</b>
                    {editInfoMode ? (
                      <input
                        className={styles.positionInput}
                        value={editUsername}
                        onChange={(e) => setEditUsername(e.target.value)}
                        placeholder="Username"
                        style={{ marginLeft: 8, minWidth: 120 }}
                        disabled={editLoading}
                      />
                    ) : (
                      <span style={{ marginLeft: 8 }}>{user.username}</span>
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}
                  >
                    <b style={{ minWidth: 130 }}>IP Address:</b>{' '}
                    <span style={{ marginLeft: 8 }}>{user.loginIp}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <b style={{ minWidth: 130 }}>Status:</b>{' '}
                    <span
                      className={`${styles.statusSwitch} ${user.isActive ? styles.active : ''
                        }`}
                      style={{
                        marginLeft: 8,
                        cursor: 'pointer',
                        opacity: statusLoading ? 0.6 : 1,
                      }}
                      onClick={statusLoading ? undefined : handleToggleStatus}
                      title="Click to toggle status"
                    >
                      <span className={styles.statusSwitchDot} />
                    </span>
                  </div>
                  {editInfoMode ? (
                    <div style={{ marginTop: 12 }}>
                      <button
                        className={styles.positionButton}
                        onClick={handleSaveEdit}
                        disabled={editLoading}
                        style={{ marginRight: 4 }}
                      >
                        {editLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className={`${styles.positionButton} secondary`}
                        onClick={handleCancelEdit}
                        disabled={editLoading}
                      >
                        Cancel
                      </button>
                      {editError && (
                        <div className={styles.error}>{editError}</div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className={styles.cashActionsSection}>
                <button
                  className={styles.cashInBtn}
                  onClick={() => {
                    setModalType('in');
                    setCashOperationError(null);
                  }}
                >
                  Cash In +
                </button>
                <button
                  className={styles.cashOutBtn}
                  onClick={() => {
                    setModalType('out');
                    setCashOperationError(null);
                  }}
                >
                  Cash Out -
                </button>
                {cashOperationError && (
                  <div
                    className={styles.error}
                    style={{ marginTop: '8px', color: 'red' }}
                  >
                    {cashOperationError}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Mobile Sub Customers Header
            <div className={styles.mobileTransactionsHeader}>
              <div className={styles.mobileTransactionsUserInfo}>
                <div className={styles.mobileTransactionsUserHeader}>
                  <div className={styles.mobileTransactionsUserName}>
                    {user.username.toUpperCase()}
                  </div>
                  <div className={styles.mobileTransactionsUserActions}>
                    <span
                      className={styles.mobileTransactionsUserAction}
                      onClick={handleEditInfo}
                    >
                      Edit Info{' '}
                      <EditIcon
                        style={{
                          verticalAlign: 'middle',
                          marginLeft: 4,
                        }}
                      />
                    </span>
                    <span
                      className={styles.mobileTransactionsUserAction}
                      onClick={() => setShowChangePassword(true)}
                    >
                      Change Password{' '}
                      <ChangePasswordIcon
                        style={{
                          verticalAlign: 'middle',
                          marginLeft: 4,
                        }}
                      />
                    </span>
                  </div>
                </div>
                <div className={styles.mobileTransactionsUserDetails}>
                  {user?.distributorName ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Distributor Name:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {user.distributorName}
                      </span>
                    </div>
                  ) : null}
                  {user?.subdistributorName ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Sub Distributor Name:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {user.subdistributorName}
                      </span>
                    </div>
                  ) : null}
                  {user?.agentName ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Agent Name:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {user.agentName}
                      </span>
                    </div>
                  ) : null}
                  {user?.shopName ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Shop Name:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {user.shopName}
                      </span>
                    </div>
                  ) : null}
                  {user?.environment ? (
                    <div className={styles.mobileDetailItem}>
                      <span className={styles.mobileDetailLabel}>
                        Environment:
                      </span>
                      <span className={styles.mobileDetailValue}>
                        {getEnvironmentDisplayName(user.environment.name)}
                      </span>
                    </div>
                  ) : null}
                  <div className={styles.mobileTransactionsDetailItem}>
                    <span className={styles.mobileTransactionsDetailLabel}>
                      Username:
                    </span>
                    <span className={styles.mobileTransactionsDetailValue}>
                      {editInfoMode ? (
                        <input
                          className={styles.positionInput}
                          value={editUsername}
                          onChange={(e) => setEditUsername(e.target.value)}
                          placeholder="Username"
                          style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                          }}
                          disabled={editLoading}
                        />
                      ) : (
                        user.username
                      )}
                    </span>
                  </div>
                  <div className={styles.mobileTransactionsDetailItem}>
                    <span className={styles.mobileTransactionsDetailLabel}>
                      IP Address:
                    </span>
                    <span className={styles.mobileTransactionsDetailValue}>
                      {user.loginIp}
                    </span>
                  </div>
                  <div className={styles.mobileTransactionsDetailItem}>
                    <span className={styles.mobileTransactionsDetailLabel}>
                      Status:
                    </span>
                    <span
                      className={`${styles.statusSwitch} ${user.isActive ? styles.active : ''
                        }`}
                      style={{
                        cursor: 'pointer',
                        opacity: statusLoading ? 0.6 : 1,
                      }}
                      onClick={statusLoading ? undefined : handleToggleStatus}
                      title="Click to toggle status"
                    >
                      <span className={styles.statusSwitchDot} />
                    </span>
                  </div>
                  {editInfoMode && (
                    <div className={styles.mobileEditActions}>
                      <button
                        className={styles.positionButton}
                        onClick={handleSaveEdit}
                        disabled={editLoading}
                        style={{
                          marginRight: 8,
                          fontSize: '12px',
                          padding: '6px 12px',
                        }}
                      >
                        {editLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className={`${styles.positionButton} secondary`}
                        onClick={handleCancelEdit}
                        disabled={editLoading}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {editError && (
                    <div className={styles.error} style={{ marginTop: 8 }}>
                      {editError}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.mobileTransactionsCashActions}>
                <button
                  className={`${styles.mobileTransactionsCashBtn} ${styles.mobileTransactionsCashInBtn}`}
                  onClick={() => {
                    setModalType('in');
                    setCashOperationError(null);
                  }}
                >
                  Cash In +
                </button>
                <button
                  className={`${styles.mobileTransactionsCashBtn} ${styles.mobileTransactionsCashOutBtn}`}
                  onClick={() => {
                    setModalType('out');
                    setCashOperationError(null);
                  }}
                >
                  Cash Out -
                </button>
              </div>
              {cashOperationError && (
                <div
                  className={styles.error}
                  style={{ marginTop: '8px', color: 'red' }}
                >
                  {cashOperationError}
                </div>
              )}
            </div>
          )}

          {/* Sub Customers Header and Search */}
          {!isMobile ? (
            <div className={styles.transactionsHeaderRow}>
              <h2 className={styles.transactionsTitle}>Sub Customers</h2>
              <div className={styles.searchByDateRow}>
                <span className={styles.searchByDateLabel}>Search by Date & Time</span>
                <div className={styles.dateSearchBox}>
                  <span className={styles.dateIcon}>📅</span>
                  <input
                    type="date"
                    className={styles.dateInput}
                    placeholder="Start Date"
                    value={subCustomersDateFrom}
                    onChange={(e) => setSubCustomersDateFrom(e.target.value)}
                  />
                  <input
                    type="time"
                    className={styles.timeInput}
                    value={subCustomersTimeFrom}
                    onChange={(e) => setSubCustomersTimeFrom(e.target.value)}
                    placeholder="Start Time"
                  />
                  <span className={styles.toLabel}>To</span>
                  <input
                    type="date"
                    className={styles.dateInput}
                    placeholder="End Date"
                    value={subCustomersDateTo}
                    onChange={(e) => setSubCustomersDateTo(e.target.value)}
                  />
                  <input
                    type="time"
                    className={styles.timeInput}
                    value={subCustomersTimeTo}
                    onChange={(e) => setSubCustomersTimeTo(e.target.value)}
                    placeholder="Finish Time"
                  />
                </div>
                <button onClick={handleSubCustomersSearch} className={styles.searchButton}>Search</button>
                {(subCustomersNameSearch || subCustomersDateFrom !== getLastWeekDate() || subCustomersDateTo !== formatDate(new Date()) || subCustomersTimeFrom !== '00:00' || subCustomersTimeTo !== '23:59') && (
                  <button className={styles.clearButton} onClick={handleClearSubCustomersFilters}>
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          ) : (
            // Mobile Sub Customers Search Section
            <>
              <div className={styles.transactionsHeaderRow}>
                <h2 className={styles.transactionsTitle}>Sub Customers</h2>
              </div>
              <div className={styles.mobileSearchSection}>
                <div className={styles.mobileSearchTitle}>Search by Date & Time</div>
                <div className={styles.mobileSearchForm}>
                  <div className={styles.mobileSearchRow}>
                    <span className={styles.mobileSearchLabel}>Start Date</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="date"
                        className={styles.mobileSearchInput}
                        value={subCustomersDateFrom}
                        onChange={(e) => setSubCustomersDateFrom(e.target.value)}
                      />
                      <input
                        type="time"
                        className={styles.mobileSearchInput}
                        value={subCustomersTimeFrom}
                        onChange={(e) => setSubCustomersTimeFrom(e.target.value)}
                        style={{ maxWidth: '120px' }}
                      />
                    </div>
                  </div>
                  <div className={styles.mobileSearchRow}>
                    <span className={styles.mobileSearchLabel}>End Date</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="date"
                        className={styles.mobileSearchInput}
                        value={subCustomersDateTo}
                        onChange={(e) => setSubCustomersDateTo(e.target.value)}
                      />
                      <input
                        type="time"
                        className={styles.mobileSearchInput}
                        value={subCustomersTimeTo}
                        onChange={(e) => setSubCustomersTimeTo(e.target.value)}
                        style={{ maxWidth: '120px' }}
                      />
                    </div>
                  </div>
                  <button className={styles.mobileSearchButton} onClick={handleSubCustomersSearch}>Search</button>
                  {(subCustomersNameSearch || subCustomersDateFrom !== getLastWeekDate() || subCustomersDateTo !== formatDate(new Date()) || subCustomersTimeFrom !== '00:00' || subCustomersTimeTo !== '23:59') && (
                    <button className={styles.mobileSearchButton} onClick={handleClearSubCustomersFilters} style={{ background: '#666', marginTop: '8px' }}>
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Sub Customers Table */}
          <TableLoading
            loading={subCustomersLoading}
            rows={8}
            columns={11}
            text="Loading sub customers..."
            className={styles.tableWrapper}
          >
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Sub Customers Name</th>
                    <th>Login Account</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Wager Enabled</th>
                    <th>Cashback enabled</th>
                    <th>Last Login Address</th>
                    <th>Last Login Time</th>
                    <th>Balance</th>
                    <th className={styles.inCol}>In</th>
                    <th className={styles.outCol}>Out</th>
                    <th className={styles.profitCol}>PROFIT</th>
                  </tr>
                </thead>
                <tbody>
                  {subCustomers.length > 0 ? (
                    subCustomers.map((subCustomer) => (
                      <tr key={subCustomer.id}>
                        <td data-label="Sub Customers Name">
                          <a
                            href="#"
                            className={styles.link}
                            onClick={(e) => {
                              e.preventDefault();
                              handleSubCustomerClick(subCustomer.id);
                            }}
                          >
                            {subCustomer.comment || subCustomer.username}
                          </a>
                        </td>
                        <td data-label="Login Account">
                          {subCustomer.username}
                        </td>
                        <td data-label="Role">
                          {subCustomer.role?.name || 'User'}
                        </td>
                        <td data-label="Status">
                          <label className={styles.switch}>
                            <input
                              type="checkbox"
                              checked={subCustomer.isActive}
                              readOnly
                            />
                            <span className={styles.slider}></span>
                          </label>
                        </td>
                        <td data-label="Wager Enabled">
                          {subCustomer.role?.name !== 'USER' ? (
                            <label className={styles.switch}>
                              <input
                                type="checkbox"
                                checked={subCustomer.isWagerEnabled || false}
                                onChange={() => handleWagerEnabledChange(subCustomer.id)}
                              />
                              <span className={styles.slider}></span>
                            </label>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td data-label="Cashback Enabled">
                          {subCustomer.role?.name !== 'USER' ? (
                            <label className={styles.switch}>
                              <input
                                type="checkbox"
                                checked={subCustomer.isWagerEnabled || false}
                                onChange={() => handleCashbackEnabledChange(subCustomer.id)}
                              />
                              <span className={styles.slider}></span>
                            </label>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td data-label="Last Login Address">
                          {subCustomer.loginIp || '-'}
                        </td>
                        <td data-label="Last Login Time">
                          {subCustomer.lastLogin
                            ? formatLoginTime(subCustomer.lastLogin)
                            : '-'}
                        </td>
                        <td data-label="Balance">
                          $
                          {formatNumberWithCommas(
                            subCustomer.Balance?.amount || subCustomer.Balance?.onHold || 0,
                          )}
                        </td>
                        <td className={styles.inCol} data-label="In">
                          +${formatNumberWithCommas(subCustomer.balanceIn || 0)}
                        </td>
                        <td className={styles.outCol} data-label="Out">
                          -$
                          {formatNumberWithCommas(subCustomer.balanceOut || 0)}
                        </td>
                        <td className={styles.profitCol} data-label="PROFIT">
                          $
                          {formatNumberWithCommas(
                            (subCustomer.balanceIn || 0) -
                            (subCustomer.balanceOut || 0),
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={11} style={{ textAlign: 'center' }}>
                        No sub customers found
                      </td>
                    </tr>
                  )}
                  <tr className={styles.totalRow}>
                    <td style={{ border: '0', background: '#fafafa' }}></td>
                    <td style={{ border: '0', background: '#fafafa' }}></td>
                    <td style={{ border: '0', background: '#fafafa' }}></td>
                    <td style={{ border: '0', background: '#fafafa' }}></td>
                    <td style={{ border: '0', background: '#fafafa' }}></td>
                    <td style={{ border: '0', background: '#fafafa' }}></td>
                    <td style={{ border: '0', background: '#fafafa' }}></td>
                    <td className={styles.totalLabel} data-label="TOTAL">
                      TOTAL
                    </td>
                    <td data-label="Balance">
                      {formatNumberWithCommas(totalBalance)}
                    </td>
                    <td className={styles.inCol} data-label="In">
                      {formatNumberWithCommas(totalBalanceIn)}
                    </td>
                    <td className={styles.outCol} data-label="Out">
                      {formatNumberWithCommas(totalBalanceOut)}
                    </td>
                    <td className={styles.profitCol} data-label="PROFIT">
                      {formatNumberWithCommas(totalProfit)}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className={styles.pagination}>
                {(subCustomersTotal && Math.ceil(subCustomersTotal / TRANSACTIONS_PAGE_SIZE) > 0) ?
                  <Stack spacing={1}>
                    <Pagination
                      onChange={(e, value) => setSubCustomersPage(value)}
                      renderItem={(item) => (
                        <PaginationItem
                          {...item}
                          slots={{
                            previous: () => (
                              <button
                                onClick={() => {
                                  if (subCustomersPage > 1) setSubCustomersPage((p) => p - 1);
                                }}
                                disabled={subCustomersPage === 1}
                                className={styles.searchBtn + " " + styles.paginationBtn}
                              >
                                &lt;
                              </button>
                            ),
                            next: () => (
                              <button
                                onClick={() => {
                                  if (subCustomersPage < Math.ceil(subCustomersTotal / TRANSACTIONS_PAGE_SIZE)) setSubCustomersPage((p) => p + 1);
                                }}
                                disabled={subCustomersPage >= Math.ceil(subCustomersTotal / TRANSACTIONS_PAGE_SIZE)}
                                className={styles.searchBtn + " " + styles.paginationBtn}
                              >
                                &gt;
                              </button>
                            ),
                          }}
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: "#313485",
                              color: "#fff",
                              padding: "5px",
                              "&:hover": { backgroundColor: "#232665" }, // darker hover
                            },
                            // "&:not(.Mui-selected)": {
                            //   border: "1px solid #313485",
                            //   color: "#313485",
                            // },
                            // borderRadius: "8px", // optional rounding
                            padding: "5px",
                          }}
                        />
                      )}
                      count={Math.ceil(subCustomersTotal / TRANSACTIONS_PAGE_SIZE)}
                      page={subCustomersPage}
                      color="primary"
                      size="medium"
                      shape="rounded"
                      siblingCount={siblingCount}
                      boundaryCount={1}
                    />
                  </Stack>
                  :
                  <button
                    disabled={transactionsPage === 1}
                    className={styles.searchBtn + ' ' + styles.paginationBtn}
                  >
                    1
                  </button>
                }
              </div>
            </div>
          </TableLoading>
        </>
      )}

      {activeSubTab === 'gameReports' && (
        // Game Reports View
        <>
          {/* User Info and Cash Actions Row */}
          <div className={styles.transactionsHeader}>
            <div className={styles.userInfoSection}>
              <div className={styles.profileEditLinks}>
                <div className={styles.profileName}>
                  {user.username.toUpperCase()}
                </div>
                <div
                  className={styles.profileLink}
                  onClick={handleEditInfo}
                  style={{ cursor: 'pointer' }}
                >
                  Edit Info{' '}
                  <EditIcon
                    style={{ verticalAlign: 'middle', marginLeft: 4 }}
                  />
                </div>
                <div
                  className={styles.profileLink}
                  onClick={() => setShowChangePassword(true)}
                  style={{ cursor: 'pointer' }}
                >
                  Change Password{' '}
                  <ChangePasswordIcon
                    style={{ verticalAlign: 'middle', marginLeft: 4 }}
                  />
                </div>
              </div>
              <div className={styles.userInfoCard}>
                {/* <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <b style={{ minWidth: 130 }}>Username:</b>
                  {editInfoMode ? (
                    <input
                      className={styles.positionInput}
                      value={editUsername}
                      onChange={(e) => setEditUsername(e.target.value)}
                      placeholder="Username"
                      style={{ marginLeft: 8, minWidth: 120 }}
                      disabled={editLoading}
                    />
                  ) : (
                    <span style={{ marginLeft: 8 }}>
                      {user.name || user.username}
                    </span>
                  )}
                </div> */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      // alignItems: 'center',
                      flexDirection: 'column',
                      marginBottom: 8,
                      gap: 8,
                    }}
                  >
                    {user?.distributorName ? <div>
                      <b style={{ minWidth: 130 }}>Distributor Name:</b>
                      <span style={{ marginLeft: 8 }}>
                        {user.distributorName}
                      </span>
                    </div> : null}
                    {user?.subdistributorName ? <div>
                      <b style={{ minWidth: 130 }}>Subdistributor Name:</b>
                      <span style={{ marginLeft: 8 }}>
                        {user.subdistributorName}
                      </span>
                    </div> : null}
                    {user?.agentName ? <div>
                      <b style={{ minWidth: 130 }}>Agent Name:</b>
                      <span style={{ marginLeft: 8 }}>
                        {user.agentName}
                      </span>
                    </div> : null}
                    {user?.username ? <div>
                      <b style={{ minWidth: 130 }}>Username:</b>
                      <span style={{ marginLeft: 8 }}>
                        {user.username}
                      </span>
                    </div> : null}
                    {user?.shopName ? <div>
                      <b style={{ minWidth: 130 }}>Shop Name:</b>
                      <span style={{ marginLeft: 8 }}>
                        {user.shopName}
                      </span>
                    </div> : null}
                    {user?.environment ? <div>
                      <b style={{ minWidth: 130 }}>Environment:</b>
                      <span style={{ marginLeft: 8 }}>
                        {getEnvironmentDisplayName(user.environment.name)}
                      </span>
                    </div> : null}

                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <b style={{ minWidth: 130 }}>IP Address:</b>{' '}
                  <span style={{ marginLeft: 8 }}>{user.loginIp}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <b style={{ minWidth: 130 }}>Status:</b>{' '}
                  <span
                    className={`${styles.statusSwitch} ${user.isActive ? styles.active : ''
                      }`}
                    style={{
                      marginLeft: 8,
                      cursor: 'pointer',
                      opacity: statusLoading ? 0.6 : 1,
                    }}
                    onClick={statusLoading ? undefined : handleToggleStatus}
                    title="Click to toggle status"
                  >
                    <span className={styles.statusSwitchDot} />
                  </span>
                </div>
                {editInfoMode ? (
                  <div style={{ marginTop: 12 }}>
                    <button
                      className={styles.positionButton}
                      onClick={handleSaveEdit}
                      disabled={editLoading}
                      style={{ marginRight: 4 }}
                    >
                      {editLoading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className={`${styles.positionButton} secondary`}
                      onClick={handleCancelEdit}
                      disabled={editLoading}
                    >
                      Cancel
                    </button>
                    {editError && (
                      <div className={styles.error}>{editError}</div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
            <div className={styles.cashActionsSection}>
              <button
                className={styles.cashInBtn}
                onClick={() => {
                  setModalType('in');
                  setCashOperationError(null);
                }}
              >
                Cash In +
              </button>
              <button
                className={styles.cashOutBtn}
                onClick={() => {
                  setModalType('out');
                  setCashOperationError(null);
                }}
              >
                Cash Out -
              </button>
              {cashOperationError && (
                <div
                  className={styles.error}
                  style={{ marginTop: '8px', color: 'red' }}
                >
                  {cashOperationError}
                </div>
              )}
            </div>
          </div>

          {/* Game Reports Header */}
          <div className={styles.transactionsHeaderRow}>
            <h2 className={styles.transactionsTitle}>Game Reports</h2>
          </div>

          {/* Game Reports Table */}
          <TableLoading
            loading={gameReportsLoading}
            rows={8}
            columns={7}
            text="Loading game reports..."
            className={styles.tableWrapper}
          >
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Game Name</th>
                    <th>Account</th>
                    <th>Game Time</th>
                    <th>Balance Before</th>
                    <th>Balance After</th>
                    <th>Profit</th>
                    <th>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {gameReports.length > 0 ? (
                    gameReports.map((gameReport) => (
                      <tr key={gameReport.id}>
                        <td data-label="Game ID">{gameReport.gameName}</td>
                        <td data-label="Account">
                          {gameReport.username || '-'}
                        </td>
                        <td data-label="Game Time">
                          {formatLoginTime(gameReport.createdAt)}
                        </td>
                        <td data-label="Balance Before">
                          ${formatNumberWithCommas(gameReport.balanceStart)}
                        </td>
                        <td data-label="Balance After">
                          ${formatNumberWithCommas(gameReport.balanceEnd || 0)}
                        </td>
                        <td
                          className={
                            (gameReport.balanceEnd || 0) -
                              gameReport.balanceStart >
                              0
                              ? styles.inCol
                              : styles.outCol
                          }
                          data-label="Profit"
                        >
                          $
                          {formatNumberWithCommas(
                            (gameReport.balanceEnd || 0) -
                            gameReport.balanceStart,
                          )}
                        </td>
                        <td data-label="IP Address">
                          {gameReport.ipAddress || '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center' }}>
                        No game reports found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* Pagination controls */}
              <div className={styles.pagination}>
                <button
                  onClick={() => {
                    if (gameReportsPage > 1) setGameReportsPage((p) => p - 1);
                  }}
                  disabled={gameReportsPage === 1}
                  className={styles.searchButton}
                >
                  &lt;
                </button>
                <span>
                  Page {gameReportsPage} of{' '}
                  {Math.ceil(gameReportsTotal / GAME_REPORTS_PAGE_SIZE) || 1}
                </span>
                <button
                  onClick={() => {
                    if (
                      gameReportsPage <
                      Math.ceil(gameReportsTotal / GAME_REPORTS_PAGE_SIZE)
                    ) {
                      setGameReportsPage((p) => p + 1);
                    }
                  }}
                  disabled={
                    gameReportsPage >=
                    Math.ceil(gameReportsTotal / GAME_REPORTS_PAGE_SIZE)
                  }
                  className={styles.searchButton}
                >
                  &gt;
                </button>
              </div>
            </div>
          </TableLoading>
        </>
      )}

      {/* Modal for Cash In/Out */}
      <Modal
        isOpen={modalType !== null}
        onClose={() => {
          setModalType(null);
          setCashOperationError(null);
        }}
      >
        <CashInOutForm
          mode={modalType === 'in' ? 'in' : 'out'}
          balance={user.Balance?.amount || user.Balance?.onHold || 0}
          userId={user.id}
          role={user.role}
          user={user}
          onSave={() => setModalType(null)}
          onCancel={() => setModalType(null)}
          onSuccess={handleCashOperationSuccess}
          onError={handleCashOperationError}
        />
      </Modal>

      {/* Report Section with Date Range Search and Summary Table */}
      {activeSubTab === 'main' && (
        <>
          {!isMobile ? (
            <div className={styles.transactionsHeaderRow}>
              <h2 className={styles.transactionsTitle}>date range</h2>
              <div className={styles.searchByDateRow}>
                <span className={styles.searchByDateLabel}>Search by Date</span>
                <div className={styles.dateSearchBox}>
                  <span className={styles.dateIcon}>📅</span>
                  <input
                    type="date"
                    className={styles.dateInput}
                    placeholder="Start Date"
                    value={reportDateFrom}
                    onChange={(e) => setReportDateFrom(e.target.value)}
                  />
                  <input
                    type="time"
                    className={styles.timeInput}
                    value={reportTimeFrom}
                    onChange={(e) => setReportTimeFrom(e.target.value)}
                    placeholder="Start Time"
                  />
                  <span className={styles.toLabel}>To</span>
                  <input
                    type="date"
                    className={styles.dateInput}
                    placeholder="End Date"
                    value={reportDateTo}
                    onChange={(e) => setReportDateTo(e.target.value)}
                  />
                  <input
                    type="time"
                    className={styles.timeInput}
                    value={reportTimeTo}
                    onChange={(e) => setReportTimeTo(e.target.value)}
                    placeholder="Finish Time"
                  />
                </div>
                <button onClick={handleReportSearch} className={styles.searchButton}>Search</button>
              </div>
            </div>
          ) : (
            <div className={styles.mobileSearchSection}>
              <div className={styles.mobileSearchTitle}>Search by Date</div>
              <div className={styles.mobileSearchForm}>
                <div className={styles.mobileSearchRow}>
                  <span className={styles.mobileSearchLabel}>Start Date</span>
                  <input
                    type="date"
                    value={reportDateFrom}
                    onChange={(e) => setReportDateFrom(e.target.value)}
                    className={styles.mobileSearchInput}
                    placeholder="Start Date"
                  />
                </div>
                <div className={styles.mobileSearchRow}>
                  <span className={styles.mobileSearchLabel}>End Date</span>
                  <input
                    type="date"
                    value={reportDateTo}
                    onChange={(e) => setReportDateTo(e.target.value)}
                    className={styles.mobileSearchInput}
                    placeholder="End Date"
                  />
                </div>
                <button onClick={handleReportSearch} className={styles.mobileSearchButton}>Search</button>
              </div>
            </div>
          )}

          {renderBalanceReportTable()}
        </>
      )}

      {/* Mobile: User Activity moved under table */}
      {activeSubTab === 'main' && isMobile && (
        <div className={styles.mobileActivitySection}>
          <div className={styles.mobileActivityCard}>
            <div className={styles.mobileActivityTitle}>User Activity</div>
            <div
              className={styles.mobileActivityList}
              ref={activityListRef}
              onScroll={handleUserActivityScroll}
            >
              {userActivityLoading ? (
                <div>Loading...</div>
              ) : userActivityError ? (
                <div className={styles.error}>{userActivityError}</div>
              ) : userActivity.length === 0 ? (
                <div>No activity found</div>
              ) : (
                <>
                  {userActivity.map((a, i) => (
                    <div key={i} className={styles.mobileActivityItem}>
                      <div className={styles.mobileActivityTime}>
                        {formatLoginTime(a.date)}
                      </div>
                      <div className={styles.mobileActivityDesc}>
                        {a.activity}
                      </div>
                    </div>
                  ))}
                  {userActivityLoadingMore && (
                    <div style={{ textAlign: 'center', padding: 8 }}>
                      Loading more...
                    </div>
                  )}
                  {!userActivityHasNextPage && userActivity.length > 0 && (
                    <div
                      style={{
                        textAlign: 'center',
                        color: '#888',
                        fontSize: 13,
                        padding: 8,
                      }}
                    >
                      No more activity
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        userId={user.id}
      />
    </main>
  );
};

export default UserPage;
